import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import API from '@/lib/api';
import Pagination, { usePagination } from '../components/Pagination';
import { useConfirm } from '@/components/ConfirmDialog';
const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
import { Plus, Trash, X, DownloadSimple, UploadSimple, MagnifyingGlass, CheckCircle, XCircle, SpinnerGap, PencilSimple, Copy, ClockCounterClockwise, Lock, LockOpen, Tag, ArrowBendUpRight, Warning, EnvelopeSimple, Phone, Globe, Buildings, User, IdentificationCard, MapPin, ChatCircleDots, Sparkle, MagicWand, CaretLeft, CaretUp, CaretDown, ShoppingCart, Bank } from '@phosphor-icons/react';
import IOSToggle from '@/components/IOSToggle';
import KlantImportModal from '../components/KlantImportModal';
import BetalingsherinneringModal from '../components/BetalingsherinneringModal';

const emptyForm = {
  type_klant: 'bedrijf',
  klantnr: '',
  btwnr: '', btw_plichtig: true, kleine_onderneming: false,
  peppol_enabled: false,
  auto_check_enabled: true,
  bedrijf: '', commerciele_naam: '', iban: '', bic: '',
  betalingstermijn: '30', verleng_einde_maand: false,
  geldigheid_offerte: '30', betaalwijze: '', standaard_btw: '21',
  munteenheid: 'EUR', grootboekrekening: '', rpr: '',
  voorn: '', achtern: '', aanspreking: '',
  voorn_bestuurder: '', achtern_bestuurder: '',
  email: '', website: '', tel: '', mobiel: '', fax: '',
  openingsuren: '',
  lat: null, lon: null,
  taal: 'nl', verstuur_methode: '', verstuur_pdf: true, auto_herinneringen: true,
  contact_kanalen: ['email', 'sms', 'whatsapp'],
  straat: '', nr: '', bus: '', postcode: '', stad: '', land: 'Belgie', tav: '',
  zelfde_levadres: true,
  lev_tav: '', lev_straat: '', lev_nr: '', lev_bus: '', lev_postcode: '', lev_stad: '', lev_land: 'Belgie', lev_tel: '',
  referentie: '', commentaar: '',
  geblokkeerd: false, kortinggroep: '', bank_keuze: '',
  verkoper_id: '',
  // Commissie-vlaggen per bron — default = true (commissie meetellen voor deze bron).
  // Admin kan per klant per bron uitzetten (bv. wholesale-klant zonder commissie).
  commissie_pos: true, commissie_b2b: true, commissie_direct: true,
  betalingskorting_actief: false, betalingskorting_dagen: '', betalingskorting_pct: '', betalingskorting_bedrag: '',
  kortingen: [], extra_velden: [],
};

const BETAALWIJZEN = ['', 'Overschrijving', 'Domiciliering', 'Contant', 'Bancontact', 'Creditcard'];

// Zoek-dropdown voor artikels, categorieën en prijsgroepen in kortingen tab
function KortingArtikelZoeker({ artikels, prijsgroepen, onAdd }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const categorieen = [...new Set(artikels.map(a => a.categorie).filter(Boolean))];
  const groepLabel = (g) => ((g.label || g.key || '').charAt(0).toUpperCase() + (g.label || g.key || '').slice(1));
  const pgItems = (prijsgroepen || []).map(g => ({ type: 'pg', label: groepLabel(g), color: g.color }));
  const ql = q.trim().toLowerCase();
  const filtered = ql ? [
    ...pgItems.filter(p => p.label.toLowerCase().includes(ql)),
    ...categorieen.filter(c => c.toLowerCase().includes(ql)).map(c => ({ type: 'cat', label: c })),
    ...artikels.filter(a => a.art_code?.toLowerCase().includes(ql) || a.omschrijving?.toLowerCase().includes(ql)).slice(0, 8).map(a => ({ type: 'art', label: a.art_code, sub: a.omschrijving })),
  ] : [
    ...pgItems,
    ...categorieen.map(c => ({ type: 'cat', label: c })),
    ...artikels.slice(0, 10).map(a => ({ type: 'art', label: a.art_code, sub: a.omschrijving })),
  ];

  const select = (item) => {
    // Prefix met type voor backend-onderscheid (PG:Kassa, CAT:Profiel) zodat kortingen-logica ze juist interpreteert.
    const prefix = item.type === 'pg' ? 'PG:' : item.type === 'cat' ? 'CAT:' : '';
    onAdd(`${prefix}${item.label}`);
    setQ('');
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input className="field-input text-xs" value={q} placeholder="Zoek artikel, categorie of prijsgroep om toe te voegen..."
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        data-testid="korting-artikel-zoeker" />
      {open && filtered.length > 0 && (
        <div className="fixed bg-white border border-gray-200 shadow-xl max-h-60 overflow-y-auto" style={{ zIndex: 9999, minWidth: 300 }}>
          {filtered.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F4FF] cursor-pointer border-b border-gray-50 text-xs"
              onMouseDown={() => select(item)}>
              {item.type === 'pg' && <>
                <span className="px-1.5 py-0.5 text-[10px] font-bold text-white shrink-0" style={{ background: item.color || '#EF4444' }}>PG</span>
                <span className="font-bold" style={{ color: item.color || '#EF4444' }}>{item.label}</span>
                <span className="text-gray-400 text-[10px]">prijsgroep</span>
              </>}
              {item.type === 'cat' && <><span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-bold shrink-0">CAT</span><span className="font-medium">{item.label}</span></>}
              {item.type === 'art' && <><span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold shrink-0">ART</span><span className="font-bold text-[#002FA7]">{item.label}</span><span className="text-gray-500 truncate">{item.sub}</span></>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
const BTW_TARIEVEN = ['0', '6', '12', '21'];
const TALEN = [
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export default function KlantenPage() {
  const confirm = useConfirm();
  const [klanten, setKlanten] = useState([]);
  // Bulk-select state — Set van klant-ids voor multi-delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  // Ref om altijd de meest recente form-waarde te kunnen lezen in async callbacks (bv. VIES lookup)
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  // Snapshot van het originele adres bij openen van klant — voor detectie van wijzigingen bij Save
  const originalAdresRef = useRef(null);
  const [formTab, setFormTab] = useState('algemeen');
  const [inst, setInst] = useState({});  // Bedrijfsinstellingen voor IBAN-keuze in klantform

  useEffect(() => { API.get('/instellingen').then(r => setInst(r.data || {})).catch(() => {}); }, []);

  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('klant_nr');  // 'klant_nr' | 'naam' | 'stad' | 'btwnr' | 'updated_at'
  const [sortDir, setSortDir] = useState('asc');  // 'asc' | 'desc'
  const [importMsg, setImportMsg] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState(false);
  const [showAiImport, setShowAiImport] = useState(false);
  const [showActiesMenu, setShowActiesMenu] = useState(false);

  // ── Per-actie zichtbaarheid (zoals op DocListPage): toggle ON/OFF + dropdown ▼
  const ALL_KLANT_ACTIONS = ['portal', 'shop_invite', 'shop_impersonate', 'shop_resetpw', 'excel', 'edit', 'duplicate', 'delete'];
  const [actiesVisible, setActiesVisible] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('acties_vis_klanten') || 'null');
      if (stored && typeof stored === 'object') return { ...Object.fromEntries(ALL_KLANT_ACTIONS.map(a => [a, true])), ...stored };
    } catch {}
    return Object.fromEntries(ALL_KLANT_ACTIONS.map(a => [a, true]));
  });
  useEffect(() => { localStorage.setItem('acties_vis_klanten', JSON.stringify(actiesVisible)); }, [actiesVisible]);
  const anyActiesOn = Object.values(actiesVisible).some(Boolean);
  const [actiesHeaderMenuOpen, setActiesHeaderMenuOpen] = useState(false);
  const [actiesHeaderMenuPos, setActiesHeaderMenuPos] = useState({ top: 0, right: 0 });
  const [bhModalOpen, setBhModalOpen] = useState(false);
  const [bhAantal, setBhAantal] = useState(0);
  const [bhTotaal, setBhTotaal] = useState(0);
  const [bhHistoriek, setBhHistoriek] = useState([]);
  const actiesHeaderMenuRef = useRef(null);
  const actiesHeaderTriggerRef = useRef(null);
  useEffect(() => {
    if (!actiesHeaderMenuOpen) return;
    const h = (e) => {
      if (actiesHeaderMenuRef.current && !actiesHeaderMenuRef.current.contains(e.target)
          && actiesHeaderTriggerRef.current && !actiesHeaderTriggerRef.current.contains(e.target)) {
        setActiesHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [actiesHeaderMenuOpen]);
  const openActiesHeaderMenu = () => {
    const r = actiesHeaderTriggerRef.current?.getBoundingClientRect();
    if (r) setActiesHeaderMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setActiesHeaderMenuOpen(o => !o);
  };
  const toggleAllActies = () => {
    const next = anyActiesOn ? Object.fromEntries(ALL_KLANT_ACTIONS.map(a => [a, false])) : Object.fromEntries(ALL_KLANT_ACTIONS.map(a => [a, true]));
    setActiesVisible(next);
  };
  const toggleActie = (key) => setActiesVisible(p => ({ ...p, [key]: !p[key] }));

  // ─── Excel-export aankoophistoriek modaal ───
  const [excelKlant, setExcelKlant] = useState(null); // klant object voor modal
  const [excelVan, setExcelVan] = useState('');
  const [excelTot, setExcelTot] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);
  const downloadAankoopExcel = async () => {
    if (!excelKlant) return;
    setExcelLoading(true);
    try {
      const params = {};
      if (excelVan) params.van = excelVan;
      if (excelTot) params.tot = excelTot;
      const r = await API.get(`/klanten/${excelKlant.id}/aankoophistoriek-excel`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const naam = (excelKlant.bedrijf || excelKlant.achtern || 'klant').replace(/[^A-Za-z0-9_-]+/g, '_');
      a.download = `Aankoophistoriek_${naam}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExcelKlant(null);
    } catch (e) {
      alert('Fout bij exporteren: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExcelLoading(false);
    }
  };
  const [vatStatus, setVatStatus] = useState(null);
  const [vatMsg, setVatMsg] = useState('');
  const [historiek, setHistoriek] = useState([]);
  const [klantSaldo, setKlantSaldo] = useState(null);
  const [klantNotities, setKlantNotities] = useState([]);
  const [klantCatalogusLog, setKlantCatalogusLog] = useState([]);
  const [klantMails, setKlantMails] = useState([]);
  const [klantMailsLoading, setKlantMailsLoading] = useState(false);
  const [klantMailDetail, setKlantMailDetail] = useState(null);
  const [klantMailDetailLoading, setKlantMailDetailLoading] = useState(false);
  const [notitieModal, setNotitieModal] = useState(null);
  const [artikels, setArtikels] = useState([]);
  // Verkopers-lijst — voor "Vaste verkoper" dropdown op klant
  const [verkopers, setVerkopers] = useState([]);
  const [prijsgroepen, setPrijsgroepen] = useState([]); // dynamisch uit /artikel-params
  const [viesBlokken, setViesBlokken] = useState({}); // klant_id → alert meta
  const [onlineZoekStatus, setOnlineZoekStatus] = useState(null); // 'loading' | 'ok' | 'error' | null
  const [onlineZoekResult, setOnlineZoekResult] = useState(null);
  const [nextKlantnr, setNextKlantnr] = useState(null); // preview voor nieuwe klant (bv. "KL-0042")

  // Zoek online via OpenStreetMap (gratis): haalt website, telefoon, openingsuren, adres op
  const zoekOnline = async () => {
    const bedrijfsnaam = (form.bedrijf || '').trim();
    if (!bedrijfsnaam) {
      alert('Vul eerst een bedrijfsnaam in of klik op "Opvragen" om via het BTW-nummer een naam te vinden.');
      return;
    }
    setOnlineZoekStatus('loading');
    setOnlineZoekResult(null);
    try {
      const r = await API.get('/klanten/zoek-online', {
        params: { bedrijf: bedrijfsnaam, stad: form.stad || '', land: form.land || '' },
      });
      if (r.data?.error) {
        setOnlineZoekStatus('error');
        setOnlineZoekResult({ error: r.data.error, google_search_url: r.data.google_search_url });
      } else {
        setOnlineZoekStatus('ok');
        setOnlineZoekResult(r.data);
      }
    } catch {
      setOnlineZoekStatus('error');
      setOnlineZoekResult({ error: 'Netwerkfout bij online zoeken' });
    }
  };

  // Neem de gevonden OSM-gegevens over in het klant-formulier
  const pasToeOnline = () => {
    if (!onlineZoekResult || onlineZoekResult.error) return;
    const r = onlineZoekResult;
    setForm(p => ({
      ...p,
      website: p.website || r.website || '',
      tel: p.tel || r.telefoon || '',
      email: p.email || r.email || '',
      openingsuren: p.openingsuren || r.openingsuren || '',
      straat: p.straat || r.straat || p.straat,
      nr: p.nr || r.nr || p.nr,
      postcode: p.postcode || r.postcode || p.postcode,
      stad: p.stad || r.gemeente || p.stad,
      lat: r.lat ? parseFloat(r.lat) : p.lat,
      lon: r.lon ? parseFloat(r.lon) : p.lon,
    }));
    setOnlineZoekResult(null);
    setOnlineZoekStatus(null);
  };

  const load = () => API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
  useEffect(() => {
    load();
    API.get('/artikels').then(r => setArtikels(r.data)).catch(() => {});
    API.get('/verkopers').then(r => setVerkopers((r.data || []).filter(v => v.actief !== false))).catch(() => setVerkopers([]));
    API.get('/artikel-params').then(r => setPrijsgroepen(r.data?.groepen || [])).catch(() => setPrijsgroepen([]));
    API.get('/klanten/geblokkeerd-vies').then(r => {
      const map = {};
      (r.data.items || []).forEach(x => { map[x.klant_id] = x; });
      setViesBlokken(map);
    }).catch(() => setViesBlokken({}));
  }, []);

  // ── Deep-link: ?id=...&tab=kortingen → open klant direct op opgegeven tab ──
  useEffect(() => {
    if (!klanten.length) return;
    const sp = new URLSearchParams(window.location.search);
    const id = sp.get('id');
    const tab = sp.get('tab');
    if (!id) return;
    const k = klanten.find(x => x.id === id);
    if (!k) return;
    startEdit(k);
    if (tab) setFormTab(tab);
    // Verwijder de query-params zodat refresh niet opnieuw opent
    window.history.replaceState({}, '', '/klanten');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klanten]);

  const exportCSV = async () => {
    try { const r = await API.get('/klanten/export', { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'klanten.csv'; a.click(); window.URL.revokeObjectURL(url); } catch { alert('Export mislukt'); }
  };
  const importCSV = async (e) => {
    const input = e.target;
    const file = input.files[0]; if (!file || importBusy) return;
    setImportError(false); setImportBusy(true); setImportMsg('Klanten importeren…');
    try {
      if (file.size > 20 * 1024 * 1024) throw new Error('Bestand te groot: maximaal 20 MB.');
      const text = await file.text();
      const { data } = await API.post('/klanten/import', text, { headers: { 'Content-Type': 'text/plain' }, timeout: 180000 });
      setImportMsg(`${data.imported.toLocaleString('nl-BE')} klanten geïmporteerd${data.failed ? ` · ${data.failed} mislukt` : ''}${data.skipped ? ` · ${data.skipped} overgeslagen` : ''}`);
      setImportError(!!data.failed);
      load();
    } catch (error) {
      setImportError(true);
      setImportMsg(error.response?.data?.detail || error.message || 'Import niet bevestigd. Controleer de klantenlijst.');
    } finally { setImportBusy(false); input.value = ''; }
  };

  const [viesBulkBusy, setViesBulkBusy] = useState(false);
  const bulkViesVerrijken = async () => {
    const kandidaten = klanten.filter(k => k.btwnr && !k.straat).length;
    if (kandidaten === 0) { toast.info('Geen klanten zonder adres met BTW-nummer gevonden.'); return; }
    if (!window.confirm(`${kandidaten} klant(en) met BTW-nummer maar zonder adres.\n\nVIES-verrijking haalt automatisch bedrijfsnaam + straat + postcode + stad op.\n\nDoorgaan?`)) return;
    setViesBulkBusy(true);
    try {
      const r = await API.post('/klanten/vies-verrijk-bulk', { alleen_lege: true, alleen_zonder_adres: true });
      const { verrijkt, ongeldig, fouten, totaal } = r.data;
      toast.success(`✨ ${verrijkt}/${totaal} klanten verrijkt via VIES${ongeldig ? ` · ${ongeldig} BTW ongeldig` : ''}${fouten ? ` · ${fouten} fouten` : ''}`);
      await load();
    } catch (e) {
      toast.error('VIES-verrijking mislukt: ' + (e?.response?.data?.detail || e?.message || 'Onbekende fout'));
    } finally {
      setViesBulkBusy(false);
    }
  };

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const uBool = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.checked }));
  const resetForm = () => { setForm({ ...emptyForm }); setEditId(null); setVatStatus(null); setVatMsg(''); setFormTab('algemeen'); setHistoriek([]); setKlantSaldo(null); setKlantNotities([]); setKlantCatalogusLog([]); setKlantMails([]); setKlantMailDetail(null); };

  // Laad de volgende klantnr-preview zodra gebruiker een nieuwe klant opent
  useEffect(() => {
    if (showForm && !editId) {
      API.get('/nummering').then(r => {
        const kl = r.data?.KL;
        if (kl?.voorbeeld) setNextKlantnr(kl.voorbeeld);
      }).catch(() => setNextKlantnr(null));
    } else {
      setNextKlantnr(null);
    }
  }, [showForm, editId]);

  const parseAddress = (addr) => {
    if (!addr) return {};
    const lines = addr.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length >= 2) {
      const m1 = lines[0].match(/^(.+?)\s+(\d+\S*)$/);
      const m2 = lines[lines.length - 1].match(/^(\d{4,5})\s+(.+)$/);
      return { straat: m1 ? m1[1] : lines[0], nr: m1 ? m1[2] : '', postcode: m2 ? m2[1] : '', stad: m2 ? m2[2] : lines[lines.length - 1] };
    }
    return { straat: addr };
  };

  // Helper: bepaal of het VIES-adres afwijkt van het huidige adres in de form
  // Retourneert een string met beslissing: 'overschrijf' | 'bewaar_als_lev' | 'annuleer'
  const _resolveAdresConflict = (parsed, curForm) => {
    const norm = (s) => (s || '').toLowerCase().replace(/[.\s,-]+/g, '').trim();
    const heeftHuidigAdres = !!(curForm.straat || curForm.postcode || curForm.stad);
    if (!heeftHuidigAdres) return 'overschrijf'; // geen conflict — vul gewoon in
    const zelfdeStraat = norm(parsed.straat) === norm(curForm.straat) && norm(parsed.nr) === norm(curForm.nr);
    const zelfdeStad = norm(parsed.postcode) === norm(curForm.postcode) && norm(parsed.stad) === norm(curForm.stad);
    if (zelfdeStraat && zelfdeStad) return 'overschrijf'; // geen effectief verschil
    // Er is een verschil — vraag user wat te doen
    const huidigStr = [curForm.straat, curForm.nr, curForm.bus].filter(Boolean).join(' ') + (curForm.postcode || curForm.stad ? ', ' + [curForm.postcode, curForm.stad].filter(Boolean).join(' ') : '');
    const viesStr = [parsed.straat, parsed.nr].filter(Boolean).join(' ') + (parsed.postcode || parsed.stad ? ', ' + [parsed.postcode, parsed.stad].filter(Boolean).join(' ') : '');
    const heeftLevAdres = !!(curForm.lev_straat && !curForm.zelfde_levadres);
    const boodschap = `⚠️ VIES-ADRES VERSCHILT VAN HUIDIG ADRES\n\n` +
      `Huidig factuuradres:\n  ${huidigStr || '(leeg)'}\n\n` +
      `VIES (maatschappelijk) adres:\n  ${viesStr || '(leeg)'}\n\n` +
      `Het VIES-adres is het JURIDISCHE adres (vaak bij boekhouder), ` +
      `niet noodzakelijk het fysieke leveradres.\n\n` +
      (heeftLevAdres
        ? `U heeft al een apart leveradres ingesteld — dat blijft onaangeroerd.\n\n`
        : `⚠️ Als u nu overschrijft, worden ook uw leveringen naar dit adres gestuurd.\n\n`) +
      `Kies:\n` +
      `  [OK] Overschrijf factuuradres met VIES\n` +
      (heeftLevAdres ? `` : `       (huidig adres wordt bewaard als apart LEVERADRES)\n`) +
      `  [Annuleren] Behoud huidig adres`;
    const ok = window.confirm(boodschap);
    if (!ok) return 'annuleer';
    return heeftLevAdres ? 'overschrijf' : 'bewaar_als_lev';
  };

  const lookupVat = useCallback(async (rawVat) => {
    const clean = rawVat.replace(/[\s.\-]/g, '').toUpperCase();
    if (clean.length < 6) { setVatStatus(null); setVatMsg(''); return; }
    setVatStatus('loading'); setVatMsg('');
    // EU-landen: doe VIES check zoals voorheen
    const EU_CODES = ['AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK'];
    const prefix = clean.slice(0, 2);
    if (EU_CODES.includes(prefix)) {
      try {
        const r = await API.get(`/vat-lookup?vat=${encodeURIComponent(clean)}`);
        if (r.data.valid) {
          setVatStatus('valid'); setVatMsg(r.data.company_name);
          const parsed = parseAddress(r.data.address);
          const nieuwLand = r.data.country === 'BE' ? 'Belgie' : (r.data.country || null);
          // Bereken beslissing ÉÉN keer buiten setForm — voorkomt double-confirm door StrictMode
          const beslissing = _resolveAdresConflict(parsed, formRef.current);
          setForm(p => {
            if (beslissing === 'annuleer') {
              // Update enkel bedrijfsnaam + land, adres onaangeroerd
              return { ...p, bedrijf: r.data.company_name || p.bedrijf, land: nieuwLand || p.land };
            }
            if (beslissing === 'bewaar_als_lev') {
              // Bewaar huidig adres als leveradres → factuuradres wordt VIES
              return {
                ...p,
                bedrijf: r.data.company_name || p.bedrijf,
                straat: parsed.straat || p.straat,
                nr: parsed.nr || p.nr,
                postcode: parsed.postcode || p.postcode,
                stad: parsed.stad || p.stad,
                land: nieuwLand || p.land,
                zelfde_levadres: false,
                lev_tav: p.lev_tav || p.tav || '',
                lev_straat: p.straat || '',
                lev_nr: p.nr || '',
                lev_bus: p.bus || '',
                lev_postcode: p.postcode || '',
                lev_stad: p.stad || '',
                lev_land: p.land || 'Belgie',
                lev_tel: p.lev_tel || p.tel || '',
              };
            }
            // 'overschrijf' — standaard gedrag
            return { ...p, bedrijf: r.data.company_name || p.bedrijf, straat: parsed.straat || p.straat, nr: parsed.nr || p.nr, postcode: parsed.postcode || p.postcode, stad: parsed.stad || p.stad, land: nieuwLand || p.land };
          });
          if (beslissing === 'bewaar_als_lev') {
            toast.success('Huidig adres bewaard als apart leveradres — factuuradres bijgewerkt via VIES');
          } else if (beslissing === 'annuleer') {
            toast.info('Factuuradres onaangeroerd — enkel bedrijfsnaam bijgewerkt');
          }
        } else { setVatStatus('invalid'); setVatMsg(r.data.error || 'Niet geldig'); }
      } catch { setVatStatus('error'); setVatMsg('Fout'); }
      return;
    }
    // Non-EU landen: format check (en voor NO ook online Brønnøysund)
    // Probeer land te detecteren uit form.land of uit eerste 2 tekens van BTW-nummer
    const landIso = (() => {
      const f = (form.land || '').toUpperCase().trim();
      if (['GB','CH','NO','AU','US','CA'].includes(f)) return f;
      if (['GB','CH','NO','AU','US','CA'].includes(prefix)) return prefix;
      return '';
    })();
    if (!landIso) {
      setVatStatus('invalid');
      setVatMsg('Onbekend land — vul het veld "Land" in met GB/CH/NO/AU/US/CA');
      return;
    }
    try {
      const r = await API.post('/vat-lookup-non-eu', { land: landIso, nummer: clean });
      const { format, online, online_beschikbaar } = r.data;
      if (format?.valid_format === false) {
        setVatStatus('invalid');
        setVatMsg(`${format.error || 'Format ongeldig'} · Voorbeeld: ${format.voorbeeld}`);
        return;
      }
      if (online?.valid) {
        // Noorwegen: officiële bevestiging
        setVatStatus('valid');
        setVatMsg(`✓ ${online.company_name}${online.vat_registered ? ' (BTW-plichtig)' : ''}`);
        const parsed = parseAddress(online.address || '');
        const beslissing = _resolveAdresConflict(parsed, formRef.current);
        setForm(p => {
          if (beslissing === 'annuleer') {
            return { ...p, bedrijf: online.company_name || p.bedrijf };
          }
          if (beslissing === 'bewaar_als_lev') {
            return {
              ...p,
              bedrijf: online.company_name || p.bedrijf,
              straat: parsed.straat || p.straat,
              nr: parsed.nr || p.nr,
              postcode: parsed.postcode || p.postcode,
              stad: parsed.stad || p.stad,
              zelfde_levadres: false,
              lev_tav: p.lev_tav || p.tav || '',
              lev_straat: p.straat || '',
              lev_nr: p.nr || '',
              lev_bus: p.bus || '',
              lev_postcode: p.postcode || '',
              lev_stad: p.stad || '',
              lev_land: p.land || 'Belgie',
              lev_tel: p.lev_tel || p.tel || '',
            };
          }
          return { ...p, bedrijf: online.company_name || p.bedrijf, straat: parsed.straat || p.straat, nr: parsed.nr || p.nr, postcode: parsed.postcode || p.postcode, stad: parsed.stad || p.stad };
        });
        if (beslissing === 'bewaar_als_lev') {
          toast.success('Huidig adres bewaard als apart leveradres — factuuradres bijgewerkt');
        } else if (beslissing === 'annuleer') {
          toast.info('Factuuradres onaangeroerd — enkel bedrijfsnaam bijgewerkt');
        }
      } else if (online && online.valid === false) {
        setVatStatus('invalid');
        setVatMsg(online.error || 'Niet gevonden in register');
      } else {
        // Format OK, maar geen online-check beschikbaar
        setVatStatus('valid');
        setVatMsg(online_beschikbaar ? '✓ Format OK (online niet bereikbaar)' : `✓ ${format.land_naam} format OK · geen gratis online-check beschikbaar`);
      }
    } catch { setVatStatus('error'); setVatMsg('Fout'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.land]);

  const startEdit = (k) => {
    const f = { ...emptyForm };
    Object.keys(f).forEach(key => { if (k[key] !== undefined) f[key] = k[key]; });
    f.zelfde_levadres = !k.lev_straat;
    if (!f.kortingen || !Array.isArray(f.kortingen)) f.kortingen = [];
    if (!f.extra_velden || !Array.isArray(f.extra_velden)) f.extra_velden = [];
    // Snapshot adres-velden om later wijzigingen te detecteren bij save
    originalAdresRef.current = {
      straat: k.straat || '', nr: k.nr || '', bus: k.bus || '',
      postcode: k.postcode || '', stad: k.stad || '', land: k.land || '',
      lev_straat: k.lev_straat || '', lev_nr: k.lev_nr || '', lev_bus: k.lev_bus || '',
      lev_postcode: k.lev_postcode || '', lev_stad: k.lev_stad || '', lev_land: k.lev_land || '',
    };
    setForm(f); setEditId(k.id); setShowForm(true); setFormTab('algemeen');
    setVatStatus(null); setVatMsg('');
    API.get(`/klanten/${k.id}/historiek`).then(r => setHistoriek(r.data)).catch(() => setHistoriek([]));
    API.get(`/klanten/${k.id}/saldo`).then(r => setKlantSaldo(r.data)).catch(() => setKlantSaldo(null));
    // Losse check op openstaande facturen — voor de betalingsherinnering-CTA (robuuster dan /saldo endpoint)
    API.get(`/klanten/${k.id}/openstaande-facturen`)
      .then(r => {
        const lijst = r.data || [];
        setBhAantal(lijst.length);
        setBhTotaal(lijst.reduce((s, f) => s + Number(f.openstaand || 0), 0));
      })
      .catch(() => { setBhAantal(0); setBhTotaal(0); });
    // Historie van eerder verstuurde herinneringen
    API.get(`/klanten/${k.id}/betalingsherinneringen`)
      .then(r => setBhHistoriek(r.data || []))
      .catch(() => setBhHistoriek([]));
    API.get(`/klanten/${k.id}/notities`, { params: { limit: 50 } }).then(r => setKlantNotities(r.data.items || [])).catch(() => setKlantNotities([]));
    API.get(`/klanten/${k.id}/catalogus-log`).then(r => setKlantCatalogusLog(r.data || [])).catch(() => setKlantCatalogusLog([]));
    // Mails — best-effort (premium feature, kan 402 retourneren)
    setKlantMailsLoading(true);
    API.get(`/klanten/${k.id}/mails`)
      .then(r => setKlantMails(r.data || []))
      .catch(() => setKlantMails([]))
      .finally(() => setKlantMailsLoading(false));
  };

  const loadKlantMails = async () => {
    if (!editId) return;
    setKlantMailsLoading(true);
    try {
      const r = await API.get(`/klanten/${editId}/mails`);
      setKlantMails(r.data || []);
    } catch (e) {
      // 402 = premium niet actief — toon lege state
      setKlantMails([]);
    }
    setKlantMailsLoading(false);
  };

  const openKlantMail = async (mid) => {
    setKlantMailDetailLoading(true);
    setKlantMailDetail({ id: mid, loading: true });
    try {
      const r = await API.get(`/mail/messages/${mid}`);
      setKlantMailDetail(r.data);
      // markeer als gelezen in lijst
      setKlantMails(arr => arr.map(m => m.id === mid ? { ...m, is_read: true } : m));
    } catch (e) {
      setKlantMailDetail(null);
      alert('Bericht kon niet geladen worden: ' + (e.response?.data?.detail || e.message));
    }
    setKlantMailDetailLoading(false);
  };

  const loadNotities = () => {
    if (!editId) return;
    API.get(`/klanten/${editId}/notities`, { params: { limit: 50 } }).then(r => setKlantNotities(r.data.items || [])).catch(() => setKlantNotities([]));
  };

  const openNotitieModal = (type = 'telefoon') => {
    const openIds = (klantSaldo?.facturen || []).filter(f => f.is_vervallen).map(f => f.id);
    const prefill = type === 'telefoon' && openIds.length > 0
      ? `Gebeld i.v.m. vervallen factu${openIds.length > 1 ? 'ren' : 'ur'} ${openIds.join(', ')}. `
      : '';
    setNotitieModal({ type, datum: new Date().toISOString().slice(0, 16), omschr: prefill, factuur_refs: openIds });
  };

  const saveNotitie = async () => {
    if (!notitieModal || !editId) return;
    if (!notitieModal.omschr.trim()) { alert('Vul een omschrijving in.'); return; }
    try {
      if (notitieModal.id) {
        await API.put(`/klanten/${editId}/notities/${notitieModal.id}`, {
          type: notitieModal.type,
          datum: new Date(notitieModal.datum).toISOString(),
          omschr: notitieModal.omschr,
          factuur_refs: notitieModal.factuur_refs || [],
        });
      } else {
        await API.post(`/klanten/${editId}/notities`, {
          type: notitieModal.type,
          datum: new Date(notitieModal.datum).toISOString(),
          omschr: notitieModal.omschr,
          factuur_refs: notitieModal.factuur_refs || [],
        });
      }
      setNotitieModal(null);
      loadNotities();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const editNotitie = (n) => {
    const d = new Date(n.datum);
    const localDt = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setNotitieModal({
      id: n.id,
      type: n.type,
      datum: localDt,
      omschr: n.omschr,
      factuur_refs: n.factuur_refs || [],
    });
  };

  const deleteNotitie = async (nid) => {
    if (!editId || !window.confirm('Deze notitie verwijderen?')) return;
    try {
      await API.delete(`/klanten/${editId}/notities/${nid}`);
      loadNotities();
    } catch (e) {
      alert('Verwijderen mislukt: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const save = async () => {
    if (!form.voorn && !form.achtern && !form.bedrijf) { alert('Vul minstens een naam of bedrijf in'); return; }
    // ── Bij bewerken van bestaande klant: detecteer adres-wijziging en waarschuw voor open documenten ──
    if (editId && originalAdresRef.current) {
      const orig = originalAdresRef.current;
      const norm = (s) => (s || '').toString().toLowerCase().replace(/[.\s,-]+/g, '').trim();
      const factuurGewijzigd =
        norm(orig.straat) !== norm(form.straat) ||
        norm(orig.nr) !== norm(form.nr) ||
        norm(orig.bus) !== norm(form.bus) ||
        norm(orig.postcode) !== norm(form.postcode) ||
        norm(orig.stad) !== norm(form.stad) ||
        norm(orig.land) !== norm(form.land);
      const effLevOrig = orig.lev_straat ? { straat: orig.lev_straat, nr: orig.lev_nr, bus: orig.lev_bus, postcode: orig.lev_postcode, stad: orig.lev_stad, land: orig.lev_land } : { straat: orig.straat, nr: orig.nr, bus: orig.bus, postcode: orig.postcode, stad: orig.stad, land: orig.land };
      const effLevNieuw = form.zelfde_levadres ? { straat: form.straat, nr: form.nr, bus: form.bus, postcode: form.postcode, stad: form.stad, land: form.land } : { straat: form.lev_straat, nr: form.lev_nr, bus: form.lev_bus, postcode: form.lev_postcode, stad: form.lev_stad, land: form.lev_land };
      const leverGewijzigd =
        norm(effLevOrig.straat) !== norm(effLevNieuw.straat) ||
        norm(effLevOrig.nr) !== norm(effLevNieuw.nr) ||
        norm(effLevOrig.bus) !== norm(effLevNieuw.bus) ||
        norm(effLevOrig.postcode) !== norm(effLevNieuw.postcode) ||
        norm(effLevOrig.stad) !== norm(effLevNieuw.stad) ||
        norm(effLevOrig.land) !== norm(effLevNieuw.land);
      if (factuurGewijzigd || leverGewijzigd) {
        try {
          const r = await API.get(`/klanten/${editId}/open-docs-count`);
          const d = r.data || {};
          if ((d.totaal || 0) > 0) {
            const orig_fact_str = [orig.straat, orig.nr].filter(Boolean).join(' ') + (orig.postcode || orig.stad ? ', ' + [orig.postcode, orig.stad].filter(Boolean).join(' ') : '');
            const nieuw_fact_str = [form.straat, form.nr].filter(Boolean).join(' ') + (form.postcode || form.stad ? ', ' + [form.postcode, form.stad].filter(Boolean).join(' ') : '');
            const orig_lev_str = [effLevOrig.straat, effLevOrig.nr].filter(Boolean).join(' ') + (effLevOrig.postcode || effLevOrig.stad ? ', ' + [effLevOrig.postcode, effLevOrig.stad].filter(Boolean).join(' ') : '');
            const nieuw_lev_str = [effLevNieuw.straat, effLevNieuw.nr].filter(Boolean).join(' ') + (effLevNieuw.postcode || effLevNieuw.stad ? ', ' + [effLevNieuw.postcode, effLevNieuw.stad].filter(Boolean).join(' ') : '');
            const detail = [];
            if (d.facturen?.count > 0) detail.push(`${d.facturen.count} openstaande factuur/facturen`);
            if (d.bestelbonnen?.count > 0) detail.push(`${d.bestelbonnen.count} open bestelbon(nen)`);
            if (d.leverbonnen?.count > 0) detail.push(`${d.leverbonnen.count} open leverbon(nen)`);
            if (d.offertes?.count > 0) detail.push(`${d.offertes.count} open offerte(s)`);
            if (d.proformas?.count > 0) detail.push(`${d.proformas.count} open proforma('s)`);
            let msg = `⚠️ ADRESWIJZIGING — OPEN DOCUMENTEN GEVONDEN\n\n`;
            msg += `Deze klant heeft nog ${d.totaal} open document(en):\n`;
            msg += `  • ${detail.join('\n  • ')}\n\n`;
            if (factuurGewijzigd) msg += `Factuuradres:\n  van: ${orig_fact_str || '(leeg)'}\n  naar: ${nieuw_fact_str || '(leeg)'}\n\n`;
            if (leverGewijzigd) msg += `Leveradres:\n  van: ${orig_lev_str || '(leeg)'}\n  naar: ${nieuw_lev_str || '(leeg)'}\n\n`;
            msg += `Deze open documenten behouden hun oude adres in de database, maar bij ` +
                   `herprinten/mailen/deelleveren kan het NIEUWE adres worden gebruikt.\n\n` +
                   `[OK] Doorgaan met wijzigen\n[Annuleren] Terug — laat adres onaangeroerd`;
            const ok = window.confirm(msg);
            if (!ok) return;
          }
        } catch (e) {
          // Silent — als de check faalt, laat de save wel doorgaan (best-effort waarschuwing)
          console.warn('open-docs-count check faalde:', e?.message);
        }
      }
    }
    const adresStr = [form.straat, form.nr].filter(Boolean).join(' ') + (form.postcode || form.stad ? ', ' + [form.postcode, form.stad].filter(Boolean).join(' ') : '');
    const payload = { ...form, adres: adresStr };
    if (form.zelfde_levadres) { ['lev_tav','lev_straat','lev_nr','lev_bus','lev_postcode','lev_stad','lev_land','lev_tel'].forEach(k => payload[k] = ''); }
    delete payload.zelfde_levadres;
    try {
      if (editId) await API.put(`/klanten/${editId}`, payload);
      else await API.post('/klanten', payload);
      resetForm(); setShowForm(false); load();
    } catch (e) { alert(e.response?.data?.detail || 'Fout bij opslaan'); }
  };

  const del = async (id) => {
    const k = klanten.find(x => x.id === id);
    const naam = k ? (k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || 'deze klant') : 'deze klant';
    const ok = await confirm({
      title: 'Klant verwijderen',
      subtitle: 'Definitief verwijderen — niet omkeerbaar.',
      message: `"${naam}" wordt verwijderd uit de klantenlijst. Bestaande documenten (facturen/offertes) blijven bewaard.`,
      tone: 'danger',
      confirmText: 'Verwijderen',
    });
    if (!ok) return;
    try {
      await API.delete(`/klanten/${id}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Fout bij verwijderen';
      // Zichtbare uitleg wanneer de klant documenten heeft (409)
      if (e?.response?.status === 409) alert('⚠️ ' + detail);
      else alert(detail);
    }
  };

  // Bulk-select helpers ─────────────────────────────
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = (visibleKlanten) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = visibleKlanten.every(k => next.has(k.id));
      if (allSelected) {
        visibleKlanten.forEach(k => next.delete(k.id));
      } else {
        visibleKlanten.forEach(k => next.add(k.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `${ids.length} klant(en) verwijderen?`,
      subtitle: 'Definitief verwijderen — niet omkeerbaar.',
      message: `${ids.length} geselecteerde klant(en) worden verwijderd.\n\nBeveiliging:\n• Systeem-klanten worden overgeslagen\n• Klanten met bestaande documenten (facturen/offertes/leverbonnen) worden overgeslagen — historische data blijft bewaard\n\nEnkel echte 'lege' klanten worden verwijderd.`,
      tone: 'danger',
      confirmText: `${ids.length} verwijderen`,
    });
    if (!ok) return;
    setBulkDeleteBusy(true);
    try {
      const r = await API.post('/klanten/bulk-delete', { ids });
      const d = r.data || {};
      let msg = d.message || 'Bulk-delete voltooid';
      if (d.blocked_has_docs > 0 && Array.isArray(d.details)) {
        const geskipt = d.details.filter(x => x.status === 'blocked_has_docs')
          .map(x => `• ${x.naam} (${x.doc_count} doc.)`).slice(0, 10).join('\n');
        msg += `\n\nOvergeslagen wegens documenten:\n${geskipt}`;
        if (d.details.filter(x => x.status === 'blocked_has_docs').length > 10) msg += '\n…';
      }
      alert(msg);
      clearSelection();
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk-delete mislukt');
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  const moveToLeverancier = async (k) => {
    const naam = k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || 'deze klant';
    const ok = await confirm({
      title: 'Klant naar leverancier verplaatsen',
      subtitle: 'Niet omkeerbaar — typ-bevestiging vereist.',
      message: `"${naam}" wordt verwijderd uit de klantenlijst en toegevoegd aan de leverancierslijst.\n\nBestaande documenten (facturen/offertes) blijven bewaard.`,
      tone: 'danger',
      confirmText: 'Verplaatsen',
      confirmWord: 'VERPLAATS',
    });
    if (!ok) return;
    try {
      const r = await API.post(`/klanten/${k.id}/naar-leverancier`);
      const msg = r.data.existed
        ? `"${r.data.naam}" bestaat al als leverancier — klantrecord is verwijderd.`
        : `"${r.data.naam}" is verplaatst naar de leverancierslijst.`;
      alert(msg);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Verplaatsen mislukt');
    }
  };
  const copyAdres = () => setForm(p => ({ ...p, lev_tav: p.tav, lev_straat: p.straat, lev_nr: p.nr, lev_bus: p.bus, lev_postcode: p.postcode, lev_stad: p.stad, lev_land: p.land, lev_tel: p.tel, zelfde_levadres: false }));

  // Kortingen helpers — elke rij = 1 artikel/categorie + eigen percentage + datum
  const addKortingRij = (artikelNaam) => {
    const nieuw = { artikel: artikelNaam || '', percentage: '', vast_bedrag: '', datum: new Date().toISOString() };
    setForm(p => ({ ...p, kortingen: [nieuw, ...(p.kortingen || [])] }));
  };
  const updKorting = (i, k, v) => setForm(p => ({ ...p, kortingen: p.kortingen.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const delKorting = (i) => setForm(p => ({ ...p, kortingen: p.kortingen.filter((_, j) => j !== i) }));

  // Extra velden helpers
  const addExtra = () => setForm(p => ({ ...p, extra_velden: [...(p.extra_velden || []), { naam: '', waarde: '' }] }));
  const updExtra = (i, k, v) => setForm(p => ({ ...p, extra_velden: p.extra_velden.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const delExtra = (i) => setForm(p => ({ ...p, extra_velden: p.extra_velden.filter((_, j) => j !== i) }));

  const [shopFilter, setShopFilter] = useState('all'); // 'all' | 'actief' | 'recent' | 'inactief30' | 'inactief90' | 'nooit'
  const [bankFilter, setBankFilter] = useState('all'); // 'all' | 'standaard' | 'iban_2'
  const [verkoperFilter, setVerkoperFilter] = useState('all'); // 'all' | 'geen' | verkoper_id
  const verkoperNaam = (vid) => (verkopers.find(v => v.id === vid) || {}).naam || '';
  const daysSince = (iso) => { if (!iso) return null; const ms = Date.now() - new Date(iso).getTime(); return Math.floor(ms / 86400000); };
  const filtered = klanten.filter(k => !q || `${k.voorn} ${k.achtern} ${k.bedrijf} ${k.btwnr} ${k.stad} ${k.klant_nr} ${verkoperNaam(k.verkoper_id)}`.toLowerCase().includes(q.toLowerCase()))
    .filter(k => {
      if (verkoperFilter === 'all') return true;
      if (verkoperFilter === 'geen') return !k.verkoper_id;
      return k.verkoper_id === verkoperFilter;
    })
    .filter(k => {
      if (shopFilter === 'all') return true;
      if (shopFilter === 'actief') return k.shop_actief && k.shop_password_hash;
      const d = daysSince(k.shop_last_login);
      if (shopFilter === 'recent') return d !== null && d <= 7;
      if (shopFilter === 'inactief30') return k.shop_actief && k.shop_password_hash && (d === null || d > 30);
      if (shopFilter === 'inactief90') return k.shop_actief && k.shop_password_hash && (d === null || d > 90);
      if (shopFilter === 'nooit') return k.shop_actief && !k.shop_last_login;
      return true;
    })
    .filter(k => {
      if (bankFilter === 'all') return true;
      const kbk = k.bank_keuze || '';
      if (bankFilter === 'standaard') return !kbk;
      return kbk === bankFilter;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let va, vb;
      if (sortBy === 'klant_nr') {
        va = parseInt(a.klant_nr) || 0;
        vb = parseInt(b.klant_nr) || 0;
        return (va - vb) * dir;
      }
      if (sortBy === 'naam') {
        va = (a.bedrijf || `${a.voorn || ''} ${a.achtern || ''}`).trim().toLowerCase();
        vb = (b.bedrijf || `${b.voorn || ''} ${b.achtern || ''}`).trim().toLowerCase();
      } else if (sortBy === 'btwnr') {
        va = (a.btwnr || '').toLowerCase();
        vb = (b.btwnr || '').toLowerCase();
      } else if (sortBy === 'stad') {
        va = (a.stad || '').toLowerCase();
        vb = (b.stad || '').toLowerCase();
      } else if (sortBy === 'shop_last_login') {
        va = a.shop_last_login || '';
        vb = b.shop_last_login || '';
        return vb.localeCompare(va) * (sortDir === 'asc' ? -1 : 1);
      } else {
        // default: updated_at/created_at (recentste eerst)
        va = a.updated_at || a.created_at || '';
        vb = b.updated_at || b.created_at || '';
        return vb.localeCompare(va) * (sortDir === 'asc' ? -1 : 1);
      }
      return va.localeCompare(vb) * dir;
    });
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'updated_at' ? 'desc' : 'asc'); }
  };
  const SortCaret = ({ col }) => sortBy !== col ? null : (sortDir === 'asc' ? <CaretUp size={10} className="inline ml-0.5" weight="bold" /> : <CaretDown size={10} className="inline ml-0.5" weight="bold" />);
  // Paginatie — gebruikers kunnen voorkeur per pagina zelf instellen (25/50/100/250 of custom)
  const { pageSize, setPageSize, curPage, setPage, paged } = usePagination(filtered, 'klanten');
  const vatBorder = vatStatus === 'valid' ? '#059669' : vatStatus === 'invalid' ? '#DC2626' : undefined;
  const VatIcon = vatStatus === 'loading' ? SpinnerGap : vatStatus === 'valid' ? CheckCircle : vatStatus === 'invalid' ? XCircle : null;
  const isBedrijf = form.type_klant === 'bedrijf';
  const adresStr = (k) => [k.straat, k.nr].filter(Boolean).join(' ') + (k.postcode || k.stad ? ', ' + [k.postcode, k.stad].filter(Boolean).join(' ') : '') || k.adres || '-';
  const formatValue = (v) => { if (v === null || v === undefined) return '(leeg)'; if (typeof v === 'object') return JSON.stringify(v); return String(v) || '(leeg)'; };

  const TABS = [
    { key: 'algemeen', label: 'Gegevens' },
    { key: 'kortingen', label: 'Kortingen' },
    { key: 'extra', label: 'Extra velden' },
    ...(editId ? [
      { key: 'saldo', label: klantSaldo?.aantal_vervallen > 0 ? `💶 Saldo (${klantSaldo.aantal_vervallen}× vervallen)` : 'Saldo & Contact' },
      { key: 'mails', label: klantMails.length > 0 ? `📧 Mails (${klantMails.length})` : '📧 Mails' },
      { key: 'catalogus', label: klantCatalogusLog.length > 0 ? `📕 Catalogus (${klantCatalogusLog.length})` : '📕 Catalogus' },
      { key: 'historiek', label: 'Wijzigingshistoriek' },
    ] : []),
  ];

  return (
    <div data-testid="klanten-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-heading mb-0">Klanten</h1>
        <div className="flex gap-2 flex-wrap">
          {!showForm && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={exportCSV} data-testid="export-klanten"><DownloadSimple size={14} /> Export</button>
              <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={bulkViesVerrijken} disabled={viesBulkBusy} data-testid="vies-bulk-verrijk-btn" title="Haal bedrijfsnaam + adres automatisch op via VIES voor alle klanten met BTW-nummer maar zonder adres">
                {viesBulkBusy ? <SpinnerGap size={14} className="animate-spin" /> : <MagicWand size={14} weight="fill" />} VIES verrijken
              </button>
              <button
                className="btn btn-secondary btn-sm inline-flex items-center gap-1"
                onClick={async () => {
                  if (!window.confirm('Verstuur shop-uitnodiging naar alle klanten met een e-mailadres die nog geen shop-account hebben?\n\nElke klant ontvangt een e-mail met een persoonlijke setup-link (48u geldig).')) return;
                  try {
                    const r = await API.post('/klanten/shop-uitnodiging-bulk', { only_inactief: true });
                    alert(`Bulk-uitnodiging voltooid:\n\n✓ ${r.data.verzonden} verzonden\n✗ ${r.data.mislukt} mislukt\n– ${r.data.skipped_no_email} zonder e-mail`);
                    load();
                  } catch (e) {
                    alert('Fout: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="shop-bulk-invite-btn"
                title="Verstuur shop-uitnodigingen naar alle klanten in bulk"
              >
                🛒 Shop bulk-uitnodiging
              </button>
              <button className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={() => setShowAiImport(true)} data-testid="ai-import-klanten-btn">
                <Sparkle size={14} weight="fill" /> AI Import
              </button>
              <label className="btn btn-secondary btn-sm cursor-pointer" data-testid="import-klanten" title="Snelle CSV-import: maximaal 50.000 klanten, 20 MB"><UploadSimple size={14} /> {importBusy ? 'Importeren…' : 'Simpel'}<input type="file" accept=".csv" className="hidden" onChange={importCSV} disabled={importBusy} data-testid="simple-customer-import-file" /></label>
            </>
          )}
          <button className="btn btn-primary" onClick={() => { if (showForm) { resetForm(); setShowForm(false); } else { resetForm(); setShowForm(true); } }} data-testid="toggle-klant-form">
            {showForm ? <><X size={14} /> Sluiten</> : <><Plus size={14} weight="bold" /> Nieuwe klant</>}
          </button>
        </div>
      </div>
      {importMsg && <div className={`mb-3 p-2 border text-sm ${importError ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`} role={importError ? 'alert' : 'status'} data-testid="customer-import-status">{importMsg}</div>}

      {/* ════ FORMULIER ════ */}
      {showForm && (
        <div className="section-card" data-testid="klant-form" data-overview-form>
          {/* Terug-knop naar klantenlijst */}
          <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-gray-100 bg-gray-50/50">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-[#002FA7] hover:bg-[#E8EEFF] rounded-md transition-colors"
              onClick={() => { resetForm(); setShowForm(false); }}
              data-testid="klant-terug-btn"
              title="Terug naar klantenlijst"
            >
              <CaretLeft size={14} weight="bold" /> Terug naar klanten
            </button>
            {editId && form.klant_nr && (
              <span className="ml-auto inline-block px-2 py-0.5 rounded-full bg-[#E8EEFF] text-[#002FA7] font-mono text-[10px] font-bold tracking-tight" data-testid="klant-form-nr-pill">
                Nr {form.klantnr || form.klant_nr}
              </span>
            )}
            {editId && (
              <button
                type="button"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border border-emerald-200 rounded-md transition-colors"
                onClick={() => { setExcelKlant({ id: editId, ...form }); setExcelVan(''); setExcelTot(''); }}
                data-testid="klant-detail-excel-btn"
                title="Excel-export aankoophistoriek (per artikel + per factuurregel)"
              >📊 Excel aankopen</button>
            )}
            {!editId && nextKlantnr && (
              <span className="ml-auto inline-block px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-mono text-[10px] font-bold tracking-tight" data-testid="klant-form-next-nr-pill" title="Nieuwe klant krijgt dit automatisch — pas aan indien nodig">
                Volgend nr: {nextKlantnr}
              </span>
            )}
          </div>
          {/* Header: Type toggle + Blokkeer + Nr */}
          <div className="flex items-center border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <button className={`flex-1 py-3 text-center text-sm font-bold transition-all ${isBedrijf ? 'bg-gradient-to-r from-[#002FA7] to-[#1E40AF] text-white shadow-inner' : 'bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
              onClick={() => setForm(p => ({ ...p, type_klant: 'bedrijf' }))} data-testid="toggle-bedrijf">
              <Buildings size={14} weight={isBedrijf ? 'fill' : 'regular'} className="inline mr-1.5" />
              Bedrijf
            </button>
            <button className={`flex-1 py-3 text-center text-sm font-bold transition-all ${!isBedrijf ? 'bg-gradient-to-r from-[#002FA7] to-[#1E40AF] text-white shadow-inner' : 'bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
              onClick={() => setForm(p => ({ ...p, type_klant: 'particulier' }))} data-testid="toggle-particulier">
              <User size={14} weight={!isBedrijf ? 'fill' : 'regular'} className="inline mr-1.5" />
              Particulier
            </button>
            {/* Blokkeer toggle */}
            <div className="px-4 flex items-center gap-2">
              <button className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border transition-colors rounded-md ${form.geblokkeerd ? 'bg-red-50 border-red-300 text-red-600' : 'bg-white border-emerald-200 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50'}`}
                onClick={async () => {
                  const nieuweStatus = !form.geblokkeerd;
                  let reden = form.geblokkeerd_reden || '';
                  if (nieuweStatus) {
                    // Bij blokkeren → vraag reden zodat het niet per ongeluk gebeurt
                    reden = window.prompt(
                      `Klant ${form.bedrijf || form.naam || 'onbekend'} BLOKKEREN?\n\n` +
                      'Geef een reden (optioneel):\n(bv. "Openstaande factuur", "Wanbetaler", "Kredietlimiet overschreden")',
                      reden || ''
                    );
                    if (reden === null) return; // Annuleer geklikt
                  } else {
                    if (!window.confirm(`Klant ${form.bedrijf || form.naam || 'onbekend'} DEBLOKKEREN?\n\nKlant kan opnieuw aankopen doen.`)) return;
                  }
                  // Direct persist naar backend — voorkomt dat user vergeet op "Opslaan" te klikken
                  if (editId) {
                    try {
                      await API.put(`/klanten/${editId}`, { geblokkeerd: nieuweStatus, geblokkeerd_reden: nieuweStatus ? (reden || '') : '' });
                      setForm(p => ({ ...p, geblokkeerd: nieuweStatus, geblokkeerd_reden: nieuweStatus ? (reden || '') : '' }));
                      toast.success(nieuweStatus ? `🚫 ${form.bedrijf || form.naam || 'Klant'} geblokkeerd` : `✅ ${form.bedrijf || form.naam || 'Klant'} gedeblokkeerd`);
                      load();
                    } catch (e) {
                      alert('Wijzigen mislukt: ' + (e.response?.data?.detail || e.message));
                    }
                  } else {
                    // Nieuwe klant: enkel form-state bijwerken, persist gebeurt bij Opslaan
                    setForm(p => ({ ...p, geblokkeerd: nieuweStatus, geblokkeerd_reden: nieuweStatus ? (reden || '') : '' }));
                  }
                }}
                data-testid="toggle-blokkeer"
                title={form.geblokkeerd ? 'Klik om te deblokkeren' : 'Klik om te blokkeren — wijziging wordt direct opgeslagen'}
              >
                {form.geblokkeerd ? <Lock size={14} weight="bold" /> : <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>}
                {form.geblokkeerd ? 'Geblokkeerd' : 'Actief'}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 px-5 pt-4 pb-3 bg-gray-50/40 border-b border-gray-200">
            {TABS.map(t => (
              <button key={t.key} className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all ${formTab === t.key ? 'bg-white text-[#002FA7] shadow border-b-2 border-[#002FA7]' : 'bg-transparent text-gray-500 hover:bg-white/60 hover:text-gray-700'}`}
                onClick={() => setFormTab(t.key)} data-testid={`klant-tab-${t.key}`}>{t.label}</button>
            ))}
          </div>

          <div className="p-5 bg-gray-50/40">
            {/* ── TAB: Gegevens (alles samen) ── */}
            {formTab === 'algemeen' && (
              <>
                {/* Geblokkeerd waarschuwing */}
                {form.geblokkeerd && <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-700 text-xs font-bold flex items-center gap-2" data-testid="blokkeer-warn"><Lock size={14} /> Deze klant is geblokkeerd — verkoop is niet mogelijk.</div>}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {/* Kolom 1: Identificatie */}
                  <div className="space-y-2.5 bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#002FA7]/10">
                      <div className="w-7 h-7 bg-[#002FA7]/10 flex items-center justify-center rounded-sm">
                        <IdentificationCard size={15} weight="duotone" className="text-[#002FA7]" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#002FA7]">Identificatie</div>
                        <div className="text-[9px] text-gray-400">Klantnr · BTW · naam · IBAN</div>
                      </div>
                    </div>
                    {/* Klantnummer — zichtbaar voor zowel bedrijf als particulier */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        Klantnummer
                        {!editId && <span className="text-[9px] font-normal text-gray-500 italic">(auto-gegenereerd, aanpasbaar)</span>}
                      </label>
                      <input
                        className="field-input font-mono text-xs"
                        value={form.klantnr || ''}
                        onChange={u('klantnr')}
                        placeholder={nextKlantnr || 'KL-0001'}
                        data-testid="klant-klantnr-input"
                        title="Dit nummer verschijnt op alle documenten voor deze klant. Laat leeg om automatisch te laten toekennen. Wijziging syncrhoniseert automatisch bestaande documenten."
                      />
                      {!editId && nextKlantnr && !form.klantnr && (
                        <div className="text-[10px] text-gray-500 italic mt-0.5">
                          Blijft leeg? → krijgt automatisch <span className="font-mono font-bold text-cyan-700">{nextKlantnr}</span>
                        </div>
                      )}
                      {editId && form.klantnr && (
                        <div className="text-[10px] text-amber-700 italic mt-0.5">
                          Wijziging wordt doorgetrokken naar alle bestaande documenten van deze klant.
                        </div>
                      )}
                    </div>
                    {isBedrijf && (
                      <>
                        <div>
                          <label className="form-label">Ondernemingsnr (BTW)</label>
                          <div className="flex gap-1.5">
                            <div className="relative flex-1">
                              <input className="field-input pr-9 font-mono text-xs" style={vatBorder ? { borderColor: vatBorder, borderWidth: 2 } : {}}
                                value={form.btwnr} onChange={u('btwnr')} onBlur={() => form.btwnr && lookupVat(form.btwnr)} placeholder="BE0123456789" data-testid="klant-btwnr" />
                              {VatIcon && <div className="absolute right-2.5 top-1/2 -translate-y-1/2"><VatIcon size={16} weight="bold" className={vatStatus === 'loading' ? 'animate-spin text-gray-400' : vatStatus === 'valid' ? 'text-[#059669]' : 'text-[#DC2626]'} /></div>}
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm whitespace-nowrap inline-flex items-center gap-1 text-[11px] px-2.5"
                              disabled={!form.btwnr || vatStatus === 'loading'}
                              onClick={() => form.btwnr && lookupVat(form.btwnr)}
                              data-testid="klant-btw-opvragen"
                              title="Valideer het BTW-nummer en vul bedrijfsnaam + FACTUURADRES automatisch in uit VIES (EU) of Brønnøysund (NO). Uw leveradres blijft onaangeroerd. E-mail en telefoon zijn niet publiek beschikbaar."
                            >
                              {vatStatus === 'loading' ? '⏳' : '🔍'} Opvragen
                            </button>
                          </div>
                          {vatMsg && <div className={`mt-0.5 text-[10px] font-medium ${vatStatus === 'valid' ? 'text-[#059669]' : 'text-[#DC2626]'}`}>{vatMsg}</div>}
                          {form.btwnr && /^be/i.test(form.btwnr.trim()) && (
                            <a
                              href={`https://www.checkinhoudingsplicht.be/?ondernemingsnummer=${encodeURIComponent(form.btwnr.replace(/^BE/i, '').replace(/[.\s]/g, ''))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-bold rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                              title="Open de officiële controle van RSZ/Fiscale inhoudingsplicht in een nieuw tabblad"
                              data-testid="check-inhouding-klant"
                            >
                              🛡️ Check Inhoudingsplicht
                            </a>
                          )}
                          {vatStatus === 'valid' && (
                            <div className="mt-0.5 text-[10px] text-gray-500 italic">
                              ℹ️ Bedrijfsnaam + <strong>factuuradres</strong> automatisch ingevuld. Uw leveradres blijft onaangeroerd. E-mail en telefoon zijn <strong>niet</strong> beschikbaar via publieke BTW-registers (privacywetgeving).
                            </div>
                          )}
                          <div className="flex gap-3 mt-1"><label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={form.btw_plichtig} onChange={uBool('btw_plichtig')} /> BTW-plichtig</label>
                          <label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={form.kleine_onderneming} onChange={uBool('kleine_onderneming')} /> Kleine ond.</label>
                          <label className="flex items-center gap-1 text-[10px]" title="Klant kan facturen ontvangen via het Peppol netwerk"><input type="checkbox" checked={!!form.peppol_enabled} onChange={uBool('peppol_enabled')} data-testid="klant-peppol" /> <span className="text-[#059669] font-semibold">Peppol aangesloten</span></label>
                          <label className="flex items-center gap-1 text-[10px]" title="Deze klant wekelijks controleren via VIES — krijgt notificatie bij wijzigingen"><input type="checkbox" checked={form.auto_check_enabled !== false} onChange={uBool('auto_check_enabled')} data-testid="klant-auto-check" /> <span className="text-[#002FA7] font-semibold">Auto VIES-check</span></label></div>
                        </div>
                        <div><label className="form-label">Maatschappelijke naam *</label>
                          <div className="flex gap-1.5">
                            <input className="field-input flex-1" value={form.bedrijf} onChange={u('bedrijf')} data-testid="klant-bedrijf" />
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm whitespace-nowrap inline-flex items-center gap-1 text-[11px] px-2.5"
                              disabled={!form.bedrijf || onlineZoekStatus === 'loading'}
                              onClick={zoekOnline}
                              data-testid="klant-zoek-online"
                              title="Zoek bedrijf op OpenStreetMap — haalt website, telefoon en openingsuren op (gratis, geen key nodig)"
                              style={{ borderColor: '#0891B2', color: '#0891B2' }}
                            >
                              {onlineZoekStatus === 'loading' ? '⏳' : '🌐'} Zoek online
                            </button>
                          </div>
                          {/* Resultaat-panel */}
                          {onlineZoekResult && (
                            <div className="mt-2 p-2 bg-cyan-50 border border-cyan-200 text-[11px]" data-testid="online-zoek-result">
                              {onlineZoekResult.error ? (
                                <>
                                  <div className="text-rose-700 font-bold mb-1">⚠️ {onlineZoekResult.error}</div>
                                  <a href={onlineZoekResult.google_search_url} target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Probeer via Google zoeken →</a>
                                </>
                              ) : (
                                <>
                                  <div className="font-bold text-cyan-900 mb-1">✓ Gevonden op OpenStreetMap — {onlineZoekResult.naam}</div>
                                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mb-1.5">
                                    {onlineZoekResult.website && <div><strong>Website:</strong> <a href={onlineZoekResult.website} target="_blank" rel="noreferrer" className="text-[#002FA7] underline truncate inline-block max-w-[200px] align-bottom">{onlineZoekResult.website}</a></div>}
                                    {onlineZoekResult.telefoon && <div><strong>Telefoon:</strong> {onlineZoekResult.telefoon}</div>}
                                    {onlineZoekResult.email && <div><strong>E-mail:</strong> {onlineZoekResult.email}</div>}
                                    {onlineZoekResult.straat && <div className="col-span-2"><strong>Adres:</strong> {onlineZoekResult.straat} {onlineZoekResult.nr}, {onlineZoekResult.postcode} {onlineZoekResult.gemeente}</div>}
                                    {onlineZoekResult.openingsuren && <div className="col-span-2"><strong>Openingsuren:</strong> <span className="whitespace-pre-line">{onlineZoekResult.openingsuren}</span></div>}
                                  </div>
                                  <div className="flex items-center gap-2 pt-1 border-t border-cyan-200">
                                    <button type="button" className="btn btn-primary btn-sm text-[10px] px-2 py-0.5" onClick={pasToeOnline} data-testid="online-zoek-pas-toe">↓ Toepassen op klant</button>
                                    <a href={onlineZoekResult.google_maps_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] underline">🗺 Open in Google Maps</a>
                                    <a href={onlineZoekResult.google_search_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] underline">🔎 Zoek op Google</a>
                                    <button type="button" className="text-[10px] text-gray-500 ml-auto" onClick={() => { setOnlineZoekResult(null); setOnlineZoekStatus(null); }}>✕ Sluiten</button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <div><label className="form-label">Commerciële naam</label><input className="field-input" value={form.commerciele_naam} onChange={u('commerciele_naam')} /></div>
                      </>
                    )}
                    {!isBedrijf && (
                      <>
                        <div><label className="form-label">Aanspreking</label><input className="field-input" value={form.aanspreking} onChange={u('aanspreking')} placeholder="Dhr. / Mevr." /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className="form-label">Voornaam</label><input className="field-input" value={form.voorn} onChange={u('voorn')} data-testid="klant-voorn" /></div>
                          <div><label className="form-label">Achternaam</label><input className="field-input" value={form.achtern} onChange={u('achtern')} data-testid="klant-achtern" /></div>
                        </div>
                      </>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="form-label">IBAN</label><input className="field-input font-mono text-[10px]" value={form.iban} onChange={u('iban')} placeholder="BE68 5390..." /></div>
                      <div><label className="form-label">BIC</label><input className="field-input font-mono text-[10px]" value={form.bic} onChange={u('bic')} /></div>
                    </div>
                    {isBedrijf && <div><label className="form-label">RPR</label><input className="field-input" value={form.rpr} onChange={u('rpr')} /></div>}
                    <div><label className="form-label">Referentie</label><input className="field-input" value={form.referentie} onChange={u('referentie')} /></div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1"><label className="form-label">Kortinggroep</label>
                        <select className="field-input" value={form.kortinggroep} onChange={u('kortinggroep')} data-testid="klant-kortinggroep">
                          <option value="">-- Kies --</option>
                          {(prijsgroepen.length > 0 ? prijsgroepen : [
                            { key: 'basic', label: 'Basic' },
                            { key: 'standard', label: 'Standard' },
                            { key: 'premium', label: 'Premium' },
                            { key: 'bulk', label: 'Bulk' },
                          ]).map(g => {
                            const lbl = (g.label || g.key || '').charAt(0).toUpperCase() + (g.label || g.key || '').slice(1);
                            return <option key={g.key} value={lbl}>{lbl}</option>;
                          })}
                        </select>
                      </div>
                      <Tag size={16} className="text-gray-300 mt-5" />
                    </div>
                    {/* Vaste verkoper voor deze klant — bij online B2B-shop bestellingen wordt de commissie
                        automatisch toegekend aan deze verkoper. Leeg = "geen vaste verkoper" (admin verwerkt). */}
                    <div>
                      <label className="form-label">Vaste verkoper (voor online bestellingen)</label>
                      <select
                        className="field-input"
                        value={form.verkoper_id || ''}
                        onChange={u('verkoper_id')}
                        data-testid="klant-verkoper"
                      >
                        <option value="">— Geen vaste verkoper —</option>
                        {verkopers.map(v => (
                          <option key={v.id} value={v.id}>{v.naam}</option>
                        ))}
                      </select>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        Bij B2B-shop bestellingen krijgt deze verkoper automatisch zijn commissie.
                      </div>
                    </div>
                    {/* Commissie-bron vinkjes — per klant kan admin aangeven welke bronnen voor commissie meetellen.
                        Default = alles aan. Uit = die bron telt NIET mee voor de verkoper's commissie. */}
                    <div className="border border-gray-200 px-3 py-2 bg-amber-50/40">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="text-[11px] font-bold uppercase text-amber-800 tracking-wide">
                          Commissie meetellen voor:
                        </div>
                        {/* Bulk-toepassen op alle klanten — handig bij eerste configuratie of beleidswijziging */}
                        <button
                          type="button"
                          className="text-[10px] text-blue-700 hover:underline font-bold"
                          title="Pas deze 3 vinkjes toe op ALLE klanten in 1 klik"
                          data-testid="klant-commissie-bulk"
                          onClick={async () => {
                            if (!window.confirm(`Toepassen op ALLE klanten?\n\n• Verkoop onderweg: ${form.commissie_pos !== false ? 'JA' : 'NEE'}\n• B2B online: ${form.commissie_b2b !== false ? 'JA' : 'NEE'}\n• Direct verkoop: ${form.commissie_direct !== false ? 'JA' : 'NEE'}`)) return;
                            try {
                              const r = await API.post('/klanten/bulk-commissie-vlaggen', {
                                commissie_pos: form.commissie_pos !== false,
                                commissie_b2b: form.commissie_b2b !== false,
                                commissie_direct: form.commissie_direct !== false,
                              });
                              toast.success(`${r.data.aangepast} klanten bijgewerkt`);
                            } catch (e) {
                              toast.error('Bulk-toepassen mislukt: ' + (e?.response?.data?.detail || e.message));
                            }
                          }}
                        >
                          📋 Toepassen op alle klanten
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 accent-[#002FA7]"
                            checked={form.commissie_pos !== false}
                            onChange={(e) => u('commissie_pos')({ target: { value: e.target.checked } })}
                            data-testid="klant-commissie-pos"
                          />
                          <span>Verkoop onderweg <span className="text-gray-500">(POS)</span></span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 accent-[#002FA7]"
                            checked={form.commissie_b2b !== false}
                            onChange={(e) => u('commissie_b2b')({ target: { value: e.target.checked } })}
                            data-testid="klant-commissie-b2b"
                          />
                          <span>B2B online bestellingen</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 accent-[#002FA7]"
                            checked={form.commissie_direct !== false}
                            onChange={(e) => u('commissie_direct')({ target: { value: e.target.checked } })}
                            data-testid="klant-commissie-direct"
                          />
                          <span>Direct verkoop <span className="text-gray-500">(manuele facturen)</span></span>
                        </label>
                      </div>
                    </div>
                    {/* Financiële korting (kassavermindering) — per klant opt-in */}
                    <div className="mt-2 p-2 border border-amber-200 bg-amber-50/40">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!form.betalingskorting_actief}
                          onChange={(e) => setForm(p => ({ ...p, betalingskorting_actief: e.target.checked }))}
                          data-testid="klant-betalingskorting"
                        />
                        <span className="text-xs font-bold text-amber-800">Financiële korting toepassen op facturen</span>
                      </label>
                      {form.betalingskorting_actief && (
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <div>
                            <label className="form-label text-[10px]">Dagen</label>
                            <input type="number" min="0" className="field-input text-xs" value={form.betalingskorting_dagen || ''} onChange={(e) => setForm(p => ({ ...p, betalingskorting_dagen: e.target.value }))} placeholder="bv. 8" data-testid="klant-betkort-dagen" />
                          </div>
                          <div>
                            <label className="form-label text-[10px]">% korting</label>
                            <input type="number" min="0" step="0.1" className="field-input text-xs" value={form.betalingskorting_pct || ''} onChange={(e) => setForm(p => ({ ...p, betalingskorting_pct: e.target.value, betalingskorting_bedrag: e.target.value ? '' : p.betalingskorting_bedrag }))} placeholder="bv. 2" data-testid="klant-betkort-pct" />
                          </div>
                          <div>
                            <label className="form-label text-[10px]">€ vast bedrag</label>
                            <input type="number" min="0" step="0.01" className="field-input text-xs" value={form.betalingskorting_bedrag || ''} onChange={(e) => setForm(p => ({ ...p, betalingskorting_bedrag: e.target.value, betalingskorting_pct: e.target.value ? '' : p.betalingskorting_pct }))} placeholder="bv. 10,00" data-testid="klant-betkort-bedrag" />
                          </div>
                        </div>
                      )}
                      <div className="text-[10px] text-gray-500 mt-1">Eén van beide invullen (% of € bedrag). Als klant tijdig betaalt → automatisch als "betaald" geboekt.</div>
                    </div>
                  </div>

                  {/* Kolom 2: Contact + Voorkeuren */}
                  <div className="space-y-2.5 bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-emerald-500/15">
                      <div className="w-7 h-7 bg-emerald-50 flex items-center justify-center rounded-sm">
                        <ChatCircleDots size={15} weight="duotone" className="text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Contact &amp; Voorkeuren</div>
                        <div className="text-[9px] text-gray-400">e-mail · tel · betaling · taal</div>
                      </div>
                    </div>
                    {isBedrijf && (
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="form-label">Voornaam (contact)</label><input className="field-input" value={form.voorn} onChange={u('voorn')} /></div>
                        <div><label className="form-label">Achternaam</label><input className="field-input" value={form.achtern} onChange={u('achtern')} /></div>
                      </div>
                    )}
                    <div><label className="form-label">E-mail</label><input className="field-input" value={form.email} onChange={u('email')} placeholder="info@bedrijf.be" data-testid="klant-email" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="form-label">Telefoon</label><input className="field-input" value={form.tel} onChange={u('tel')} data-testid="klant-tel" /></div>
                      <div><label className="form-label">Mobiel</label><input className="field-input" value={form.mobiel} onChange={u('mobiel')} /></div>
                    </div>
                    <div><label className="form-label">Website</label><input className="field-input" value={form.website} onChange={u('website')} /></div>
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        Openingsuren
                        <span className="text-[9px] font-normal text-gray-400">(handig voor leveringen)</span>
                      </label>
                      <textarea
                        className="field-input text-[11px] font-mono"
                        rows={3}
                        value={form.openingsuren}
                        onChange={u('openingsuren')}
                        placeholder={'Ma-Vr 08:30-17:00\nZa 09:00-12:00'}
                        data-testid="klant-openingsuren"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><label className="form-label">Termijn</label><input type="number" className="field-input" value={form.betalingstermijn} onChange={u('betalingstermijn')} /></div>
                      <div><label className="form-label">BTW %</label><select className="field-input" value={form.standaard_btw} onChange={u('standaard_btw')}>{BTW_TARIEVEN.map(b => <option key={b} value={b}>{b}%</option>)}</select></div>
                      <div>
                        <label className="form-label flex items-center justify-between">
                          <span>Documenttaal</span>
                          <span className="text-[9px] text-gray-400 font-normal" title="Facturen, offertes, e-mails en factoring-bijlagen worden automatisch in deze taal gegenereerd">📄 auto</span>
                        </label>
                        <select className="field-input" value={form.taal} onChange={u('taal')} data-testid="klant-taal-select">
                          {TALEN.map(t => <option key={t.code} value={t.code}>{t.flag} {t.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div><label className="form-label">Betaalwijze</label><select className="field-input" value={form.betaalwijze} onChange={u('betaalwijze')}>{BETAALWIJZEN.map(b => <option key={b} value={b}>{b || '-- Kies --'}</option>)}</select></div>
                    {inst.iban_2 && (
                      <div>
                        <label className="form-label flex items-center gap-1.5">
                          Bank-rekening op factuur
                          <span className="text-[9px] font-normal text-gray-400">(welke IBAN tonen?)</span>
                          {inst.factoring_actief && form.bank_keuze === 'iban_2' && (
                            <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 bg-orange-500 text-white inline-flex items-center gap-0.5" title="IBAN Factoring actief — facturen worden via factor verwerkt">
                              <Bank size={10} weight="bold" /> FACTOR
                            </span>
                          )}
                        </label>
                        <select className="field-input" value={form.bank_keuze || ''} onChange={u('bank_keuze')} data-testid="klant-bank-keuze">
                          <option value="">{`Standaard (${inst.iban || ''})`}</option>
                          <option value="iban_2">{`${inst.iban_2_label || 'Alternatieve rekening'} (${inst.iban_2 || ''})`}</option>
                        </select>
                        {inst.factoring_actief && form.bank_keuze === 'iban_2' && (
                          <div className="mt-1 text-[10px] text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
                            🏦 Deze klant ontvangt facturen via <strong>IBAN Factoring</strong>: cessie-tekst op factuur, introductiebrief als bijlage, en CC naar factoringmaatschappij.
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="form-label flex items-center gap-1.5">Contact-kanalen <span className="text-[9px] font-normal text-gray-400">(herinneringen/betaalbevestiging)</span></label>
                      <div className="grid grid-cols-2 gap-1.5 p-2 border border-gray-200 bg-gray-50/50">
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-white px-1.5 py-1 transition-colors">
                          <input type="checkbox"
                            checked={(form.contact_kanalen || ['email','sms','whatsapp']).includes('email')}
                            onChange={e => {
                              const cur = form.contact_kanalen || ['email','sms','whatsapp'];
                              setForm(p => ({ ...p, contact_kanalen: e.target.checked ? [...new Set([...cur, 'email'])] : cur.filter(k => k !== 'email') }));
                            }}
                            data-testid="klant-kanaal-email" />
                          ✉️ E-mail
                        </label>
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-white px-1.5 py-1 transition-colors">
                          <input type="checkbox"
                            checked={(form.contact_kanalen || ['email','sms','whatsapp']).includes('sms')}
                            onChange={e => {
                              const cur = form.contact_kanalen || ['email','sms','whatsapp'];
                              setForm(p => ({ ...p, contact_kanalen: e.target.checked ? [...new Set([...cur, 'sms'])] : cur.filter(k => k !== 'sms') }));
                            }}
                            data-testid="klant-kanaal-sms" />
                          📱 SMS
                        </label>
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-white px-1.5 py-1 transition-colors">
                          <input type="checkbox"
                            checked={(form.contact_kanalen || ['email','sms','whatsapp']).includes('whatsapp')}
                            onChange={e => {
                              const cur = form.contact_kanalen || ['email','sms','whatsapp'];
                              setForm(p => ({ ...p, contact_kanalen: e.target.checked ? [...new Set([...cur, 'whatsapp'])] : cur.filter(k => k !== 'whatsapp') }));
                            }}
                            data-testid="klant-kanaal-whatsapp" />
                          💬 WhatsApp
                        </label>
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-white px-1.5 py-1 transition-colors col-span-2 border-t border-gray-200 pt-1.5 mt-0.5">
                          <input type="checkbox"
                            checked={(form.contact_kanalen || ['email','sms','whatsapp']).length === 0}
                            onChange={e => setForm(p => ({ ...p, contact_kanalen: e.target.checked ? [] : ['email','sms','whatsapp'] }))}
                            data-testid="klant-kanaal-geen" />
                          🚫 <span className="font-semibold">Geen automatische herinneringen</span> (schakelt alles uit)
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-1.5 text-[11px]"><input type="checkbox" checked={form.verstuur_pdf} onChange={uBool('verstuur_pdf')} className="accent-[#002FA7]" /> Verstuur PDF</label>
                      <label className="flex items-center gap-1.5 text-[11px]"><input type="checkbox" checked={form.auto_herinneringen} onChange={uBool('auto_herinneringen')} className="accent-[#002FA7]" /> Auto herinneringen</label>
                    </div>
                  </div>

                  {/* Kolom 3: Adressen */}
                  <div className="space-y-2.5 bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-amber-500/15">
                      <div className="w-7 h-7 bg-amber-50 flex items-center justify-center rounded-sm">
                        <MapPin size={15} weight="duotone" className="text-amber-700" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Facturatieadres <span className="text-[9px] font-normal text-gray-400 normal-case">(juridisch · VIES)</span></div>
                        <div className="text-[9px] text-gray-400">Maatschappelijke zetel — verschijnt op facturen</div>
                      </div>
                      <button className="text-[10px] text-[#002FA7] font-medium flex items-center gap-0.5 hover:underline" onClick={copyAdres} title="Kopieer dit adres naar het leveradres">
                        <Copy size={10} /> → Leveradres
                      </button>
                    </div>
                    <div><label className="form-label">T.a.v.</label><input className="field-input" value={form.tav} onChange={u('tav')} /></div>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="col-span-2"><label className="form-label">Straat</label><input className="field-input" value={form.straat} onChange={u('straat')} data-testid="klant-straat" /></div>
                      <div><label className="form-label">Nr</label><input className="field-input" value={form.nr} onChange={u('nr')} /></div>
                      <div><label className="form-label">Bus</label><input className="field-input" value={form.bus} onChange={u('bus')} /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><label className="form-label">Postcode</label><input className="field-input" value={form.postcode} onChange={u('postcode')} data-testid="klant-postcode" /></div>
                      <div><label className="form-label">Gemeente</label><input className="field-input" value={form.stad} onChange={u('stad')} data-testid="klant-stad" /></div>
                      <div><label className="form-label">Land</label><input className="field-input" value={form.land} onChange={u('land')} /></div>
                    </div>

                    <div className="mt-3 pt-2 border-t border-gray-100">
                      <label className="flex items-center justify-between mb-2 cursor-pointer">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                          Leveradres <span className="text-[9px] font-normal text-gray-400 normal-case">(fysieke locatie)</span>
                        </span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-600" title="Vink AAN als goederen naar hetzelfde adres als factuur mogen · Vink UIT om een apart magazijn/werf-adres in te stellen">
                          <input type="checkbox" checked={form.zelfde_levadres} onChange={uBool('zelfde_levadres')} className="accent-[#002FA7]" data-testid="klant-zelfde-levadres" />
                          Zelfde als factuur
                        </span>
                      </label>
                      {!form.zelfde_levadres && (
                        <div className="space-y-2 p-2.5 bg-[#ECFDF5] border-2 border-[#10B981] rounded">
                          <div className="text-[10px] font-semibold text-emerald-800 flex items-center gap-1 mb-1">
                            📦 Goederen worden naar dit adres geleverd (niet naar factuuradres)
                          </div>
                          <div><label className="form-label">T.a.v.</label><input className="field-input" value={form.lev_tav} onChange={u('lev_tav')} data-testid="klant-lev-tav" /></div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="col-span-2"><label className="form-label">Straat</label><input className="field-input" value={form.lev_straat} onChange={u('lev_straat')} data-testid="klant-lev-straat" /></div>
                            <div><label className="form-label">Nr</label><input className="field-input" value={form.lev_nr} onChange={u('lev_nr')} data-testid="klant-lev-nr" /></div>
                            <div><label className="form-label">Bus</label><input className="field-input" value={form.lev_bus} onChange={u('lev_bus')} /></div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div><label className="form-label">Postcode</label><input className="field-input" value={form.lev_postcode} onChange={u('lev_postcode')} data-testid="klant-lev-postcode" /></div>
                            <div><label className="form-label">Gemeente</label><input className="field-input" value={form.lev_stad} onChange={u('lev_stad')} data-testid="klant-lev-stad" /></div>
                            <div><label className="form-label">Land</label><input className="field-input" value={form.lev_land} onChange={u('lev_land')} /></div>
                          </div>
                          <div className="text-[9px] text-emerald-700/70 italic pt-1">
                            💡 VIES-lookups raken dit adres nooit aan — enkel het factuuradres.
                          </div>
                        </div>
                      )}
                      {/* ── Locatie-kaart (altijd tonen als er coords OF adres beschikbaar is) ── */}
                      {(() => {
                        const hasCoords = form.lat && form.lon;
                        const adresStr = [form.straat, form.nr, form.postcode, form.stad, form.land].filter(Boolean).join(' ').trim();
                        const fallbackQuery = adresStr || (form.bedrijf || '').trim();
                        if (!hasCoords && !fallbackQuery) return null;
                        // Bij coords: OSM embed (geen API key, exact)
                        let bbox = '', marker = '';
                        if (hasCoords) {
                          const lat = parseFloat(form.lat), lon = parseFloat(form.lon);
                          const d = 0.003;
                          bbox = `${lon - d},${lat - d},${lon + d},${lat + d}`;
                          marker = `${lat},${lon}`;
                        }
                        const osmUrl = hasCoords
                          ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`
                          : null;
                        // Fallback: Google Maps embed (werkt zonder API key voor basis-search)
                        const gMapsEmbed = !hasCoords
                          ? `https://maps.google.com/maps?q=${encodeURIComponent(fallbackQuery)}&output=embed&hl=nl`
                          : null;
                        const gMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fallbackQuery)}`;
                        return (
                          <div className="mt-3 border border-gray-200 overflow-hidden" data-testid="klant-locatie-kaart">
                            <div className="px-2 py-1 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">📍 Locatie</span>
                              <a href={gMapsUrl} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline">Route / Streetview ↗</a>
                            </div>
                            {osmUrl ? (
                              <iframe
                                src={osmUrl}
                                title="Klant-locatie op OpenStreetMap"
                                style={{ width: '100%', height: 200, border: 0, display: 'block' }}
                                loading="lazy"
                                data-testid="klant-osm-iframe"
                              />
                            ) : (
                              <iframe
                                src={gMapsEmbed}
                                title="Klant-locatie op Google Maps"
                                style={{ width: '100%', height: 200, border: 0, display: 'block' }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen=""
                                data-testid="klant-gmaps-iframe"
                              />
                            )}
                            <div className="px-2 py-1 bg-gray-50 border-t border-gray-200 text-[9px] text-gray-400 flex items-center justify-between">
                              {hasCoords ? (
                                <>
                                  <span>Coördinaten: {parseFloat(form.lat).toFixed(5)}, {parseFloat(form.lon).toFixed(5)}</span>
                                  <a href={`https://www.openstreetmap.org/?mlat=${form.lat}&mlon=${form.lon}#map=17/${form.lat}/${form.lon}`} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">Groter ↗</a>
                                </>
                              ) : (
                                <>
                                  <span className="italic">Zoekopdracht: "{fallbackQuery.slice(0, 40)}{fallbackQuery.length > 40 ? '…' : ''}"</span>
                                  <span className="text-gray-400">Klik "🌐 Zoek online" voor exacte coördinaten</span>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="mt-4"><label className="form-label">Commentaar</label><textarea className="field-input text-sm" rows={2} value={form.commentaar} onChange={u('commentaar')} placeholder="Interne notitie..." data-testid="klant-commentaar" /></div>
              </>
            )}

            {/* ── TAB: Kortingen ── */}
            {formTab === 'kortingen' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold">Klantspecifieke kortingen</span>
                </div>
                {/* Zoeker bovenaan om artikel/categorie/prijsgroep toe te voegen */}
                <div className="mb-4 p-3 bg-[#F8FAFC] border border-gray-200">
                  <label className="form-label">Artikel, categorie of prijsgroep toevoegen</label>
                  <KortingArtikelZoeker artikels={artikels} prijsgroepen={prijsgroepen} onAdd={(art) => addKortingRij(art)} />
                  <button className="btn btn-ghost btn-sm mt-2 text-xs text-green-600" onClick={() => addKortingRij('')} data-testid="add-korting-alle">
                    <Plus size={12} /> Korting op alle artikels
                  </button>
                </div>
                {/* Lijst van kortingen */}
                {(form.kortingen || []).length > 0 ? (
                  <table className="data-table">
                    <thead><tr><th>Artikel / Categorie / Prijsgroep</th><th style={{width:100}} className="right">Korting %</th><th style={{width:110}} className="right">Vaste prijs €</th><th style={{width:130}}>Aangemaakt</th><th style={{width:36}}></th></tr></thead>
                    <tbody>
                      {(form.kortingen || []).map((k, i) => {
                        const isPG = (k.artikel || '').startsWith('PG:');
                        const isCAT = (k.artikel || '').startsWith('CAT:');
                        const isART = k.artikel && !isPG && !isCAT && artikels.some(a => a.art_code === k.artikel);
                        const cleanLabel = (k.artikel || '').replace(/^(PG:|CAT:)/, '');
                        const tagLabel = isPG ? 'PG' : isCAT || (!isART && k.artikel) ? 'CAT' : isART ? 'ART' : '';
                        const tagColor = isPG ? 'bg-red-50 text-red-600' : (isCAT || (!isART && k.artikel)) ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600';
                        const artOmschr = isART ? (artikels.find(a => a.art_code === k.artikel)?.omschrijving || '') : '';
                        const subLabel = isART ? artOmschr : isPG ? 'prijsgroep' : k.artikel ? 'categorie' : '';
                        return (
                        <tr key={i}>
                          <td>
                            {k.artikel ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold ${tagColor}`}>{tagLabel}</span>
                                {cleanLabel}
                                {subLabel && <span className="text-gray-500 font-normal truncate" style={{ maxWidth: 520 }} title={subLabel} data-testid={`korting-omschr-${i}`}>{subLabel}</span>}
                              </span>
                            ) : (
                              <span className="text-xs text-green-600 font-medium">Alle artikels</span>
                            )}
                          </td>
                          <td><input type="number" className="field-input text-xs text-right" value={k.percentage} onChange={e => updKorting(i, 'percentage', e.target.value)} placeholder="10" data-testid={`korting-pct-${i}`} /></td>
                          <td><input type="number" className="field-input text-xs text-right" value={k.vast_bedrag} onChange={e => updKorting(i, 'vast_bedrag', e.target.value)} placeholder="0.00" /></td>
                          <td className="text-[10px] text-gray-400 font-mono">{k.datum ? new Date(k.datum).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td><button className="btn btn-ghost btn-sm text-red-400" onClick={() => delKorting(i)} data-testid={`del-korting-${i}`}><Trash size={12} /></button></td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center text-gray-400 py-6 text-sm">Geen kortingen. Zoek een artikel, categorie of prijsgroep hierboven om toe te voegen.</div>
                )}
                <p className="text-[10px] text-gray-400 mt-3">Voorrang: specifiek artikel &gt; categorie &gt; prijsgroep &gt; alle artikels. Prijsgroep-kortingen gelden enkel voor de artikels die onder die prijsgroep vallen.<br/><strong>Vaste prijs €</strong> = de klant betaalt dit artikel altijd aan dit bedrag (vaste nettoprijs, per lijn zoals op het document). Heeft voorrang op Korting %.</p>
              </div>
            )}

            {/* ── TAB: Extra velden ── */}
            {formTab === 'extra' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold">Extra velden</span>
                  <button className="btn btn-secondary btn-sm" onClick={addExtra} data-testid="add-extra"><Plus size={12} /> Veld toevoegen</button>
                </div>
                {(form.extra_velden || []).length > 0 ? (
                  <div className="space-y-2">
                    {(form.extra_velden || []).map((v, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input className="field-input text-xs flex-1" value={v.naam} onChange={e => updExtra(i, 'naam', e.target.value)} placeholder="Veldnaam" />
                        <input className="field-input text-xs flex-1" value={v.waarde} onChange={e => updExtra(i, 'waarde', e.target.value)} placeholder="Waarde" />
                        <button className="btn btn-ghost btn-sm text-red-400" onClick={() => delExtra(i)}><Trash size={12} /></button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8 text-sm">Geen extra velden. Voeg aangepaste velden toe voor deze klant.</div>
                )}
              </div>
            )}

            {/* ── TAB: Saldo & Contact ── */}
            {formTab === 'saldo' && (
              <div className="space-y-4" data-testid="saldo-tab">
                {/* KPI-tiles */}
                <div className="grid grid-cols-4 gap-3">
                  <div className={`p-3 border-l-4 ${klantSaldo?.aantal_vervallen > 0 ? 'bg-red-50 border-red-500' : 'bg-gray-50 border-gray-300'}`}>
                    <div className="text-[10px] uppercase text-gray-600 font-bold">Openstaand</div>
                    <div className="text-lg font-bold font-mono mt-1">{fmt(klantSaldo?.openstaand_totaal || 0)}</div>
                    <div className="text-[10px] text-gray-500">{klantSaldo?.aantal_openstaand || 0} factuur/facturen</div>
                  </div>
                  <div className={`p-3 border-l-4 ${klantSaldo?.aantal_vervallen > 0 ? 'bg-red-50 border-red-500' : 'bg-green-50 border-green-500'}`}>
                    <div className="text-[10px] uppercase text-gray-600 font-bold">Vervallen</div>
                    <div className={`text-lg font-bold font-mono mt-1 ${klantSaldo?.aantal_vervallen > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {fmt(klantSaldo?.vervallen_totaal || 0)}
                    </div>
                    <div className="text-[10px] text-gray-500">{klantSaldo?.aantal_vervallen || 0} vervallen</div>
                  </div>
                  <div className="p-3 border-l-4 bg-blue-50 border-blue-500">
                    <div className="text-[10px] uppercase text-gray-600 font-bold">Contacten</div>
                    <div className="text-lg font-bold font-mono mt-1">{klantNotities.length}</div>
                    <div className="text-[10px] text-gray-500">historiek totaal</div>
                  </div>
                  <div className="p-3 border-l-4 bg-purple-50 border-purple-500 flex flex-col justify-center">
                    <button className="btn btn-primary btn-sm w-full" onClick={() => openNotitieModal('telefoon')} data-testid="klant-log-gesprek-btn">
                      ✎ Nieuw contact loggen
                    </button>
                    {(form.tel || form.gsm) && (
                      <a href={`tel:${form.tel || form.gsm}`} className="text-[11px] font-bold text-[#002FA7] hover:underline mt-1.5 text-center">
                        📞 {form.tel || form.gsm}
                      </a>
                    )}
                    {(form.tel || form.gsm || form.mobiel) && form.id && (
                      <a
                        href={`/whatsapp?klant_id=${form.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] font-bold text-[#25D366] hover:underline mt-1 text-center inline-flex items-center justify-center gap-1"
                        data-testid="klant-whatsapp-btn"
                        title="Open WhatsApp Hub voor deze klant"
                      >
                        💬 Stuur WhatsApp
                      </a>
                    )}
                  </div>
                </div>

                {/* Betalingsherinnering CTA — enkel zichtbaar bij openstaande facturen */}
                {bhAantal > 0 && editId && (
                  <button
                    className="w-full py-3 px-4 bg-gradient-to-r from-[#B91C1C] to-[#EA580C] hover:from-[#991B1B] hover:to-[#C2410C] text-white font-bold flex items-center justify-between transition-all shadow-md hover:shadow-lg"
                    onClick={() => setBhModalOpen(true)}
                    data-testid="klant-open-betalingsherinnering"
                  >
                    <span className="flex items-center gap-2">
                      <Warning size={20} weight="fill" />
                      <span>Betalingsherinnering opmaken</span>
                    </span>
                    <span className="text-xs opacity-80">→ {bhAantal} openstaand · {fmt(bhTotaal)}</span>
                  </button>
                )}

                {/* Historiek van verstuurde betalingsherinneringen */}
                {bhHistoriek.length > 0 && (
                  <div className="border border-gray-200" data-testid="bh-historiek">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <div className="text-[10px] font-black uppercase tracking-wider text-gray-500">Herinneringen-historiek</div>
                      <div className="text-[10px] text-gray-400">{bhHistoriek.length} × verstuurd</div>
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                      {bhHistoriek.map(h => {
                        const NIV = { 1: { l: '1e Herinnering', k: '#002FA7' }, 2: { l: '2e Herinnering', k: '#EA580C' }, 3: { l: 'Ingebrekestelling', k: '#B91C1C' } };
                        const n = NIV[h.niveau] || NIV[1];
                        const KANAAL_ICON = { pdf: '📄', email: '✉️', whatsapp: '💬' };
                        const d = h.datum ? new Date(h.datum) : null;
                        return (
                          <div key={h.id} className="px-3 py-2 flex items-center gap-3 text-xs hover:bg-gray-50" data-testid={`bh-hist-${h.id}`}>
                            <span className="text-lg" title={h.kanaal}>{KANAAL_ICON[h.kanaal] || '📄'}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-bold" style={{ color: n.k }}>{n.l}</span>
                                <span className="text-[9px] font-bold px-1.5 py-0.5 bg-gray-100 text-gray-600 uppercase">{h.kanaal || 'pdf'}</span>
                                {h.ontvanger && <span className="text-[10px] text-gray-500 font-mono truncate">→ {h.ontvanger}</span>}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {d ? d.toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                {' · '}
                                {(h.factuur_nummers || []).length || (h.factuur_ids || []).length} factuur/facturen
                              </div>
                            </div>
                            <div className="text-right text-xs font-bold text-red-600">{fmt(h.totaal_openstaand || 0)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}


                {/* Openstaande facturen */}
                {klantSaldo?.facturen?.length > 0 ? (
                  <div className="border border-gray-200">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider">
                      Openstaande facturen (vervallen eerst)
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Factuur</th>
                          <th className="px-3 py-2 text-left">Datum</th>
                          <th className="px-3 py-2 text-left">Verval</th>
                          <th className="px-3 py-2 text-right">Totaal</th>
                          <th className="px-3 py-2 text-right">Credit</th>
                          <th className="px-3 py-2 text-right">Betaald</th>
                          <th className="px-3 py-2 text-right">Openstaand</th>
                        </tr>
                      </thead>
                      <tbody>
                        {klantSaldo.facturen.map(f => (
                          <tr key={f.id} className={`border-b border-gray-100 ${f.is_vervallen ? 'bg-red-50/50' : ''}`}>
                            <td className="px-3 py-2 font-mono font-bold">
                              <button className="text-[#002FA7] hover:underline" onClick={() => window.open(`/documenten/${f.id}/bewerk`, '_blank')}>{f.id}</button>
                            </td>
                            <td className="px-3 py-2 text-gray-500">{f.datum}</td>
                            <td className="px-3 py-2">
                              <span className={f.is_vervallen ? 'text-red-700 font-bold' : 'text-gray-500'}>
                                {f.verval} {f.dagen_over_verval > 0 ? `(${f.dagen_over_verval}d te laat)` : `(nog ${Math.abs(f.dagen_over_verval || 0)}d)`}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{fmt(f.totaal)}</td>
                            <td className="px-3 py-2 text-right font-mono text-rose-600">{f.creditnota_totaal > 0 ? `-${fmt(f.creditnota_totaal)}` : '—'}</td>
                            <td className="px-3 py-2 text-right font-mono text-green-600">{f.betaald_bedrag > 0 ? fmt(f.betaald_bedrag) : '—'}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold">{fmt(f.openstaand)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 bg-green-50 border border-green-200 text-center text-xs text-green-800">
                    ✓ Geen openstaande facturen — deze klant heeft alles betaald.
                  </div>
                )}

                {/* Contact-historiek */}
                <div className="border border-gray-200">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider flex items-center justify-between">
                    <span>Contact-historiek</span>
                    <span className="text-gray-500 font-normal normal-case">{klantNotities.length} entries</span>
                  </div>
                  {klantNotities.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {klantNotities.map(n => {
                        const d = new Date(n.datum);
                        const icon = n.type === 'telefoon' ? '📞' : n.type === 'email' ? '✉️' : n.type === 'afspraak' ? '📅' : n.type === 'notitie' ? '📝' : '•';
                        return (
                          <div key={n.id} className="px-3 py-2 text-xs hover:bg-gray-50">
                            <div className="flex items-start gap-2">
                              <span className="text-base shrink-0">{icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                  <span className="font-mono">{d.toLocaleString('nl-BE')}</span>
                                  <span className="uppercase tracking-wider">{n.type}</span>
                                  {n.factuur_refs?.length > 0 && (
                                    <span className="inline-flex gap-1">
                                      {n.factuur_refs.map(r => <span key={r} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 font-mono">{r}</span>)}
                                    </span>
                                  )}
                                  <span className="ml-auto opacity-60">door {n.aangemaakt_door}</span>
                                  <button
                                    className="text-[#002FA7] hover:bg-blue-50 px-1"
                                    onClick={() => editNotitie(n)}
                                    title="Bewerken"
                                    data-testid={`edit-klant-notitie-${n.id}`}
                                  >✎</button>
                                  <button
                                    className="text-red-400 hover:text-red-600"
                                    onClick={() => deleteNotitie(n.id)}
                                    title="Verwijderen"
                                  >×</button>
                                </div>
                                <div className="text-gray-800 mt-0.5 whitespace-pre-wrap">{n.omschr}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-xs text-gray-400">Nog geen contact geregistreerd. Klik "Nieuw contact loggen" om te starten.</div>
                  )}
                </div>
              </div>
            )}

            {/* ── TAB: Historiek ── */}
            {formTab === 'catalogus' && (
              <div data-testid="klant-catalogus-tab">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold text-gray-600 uppercase tracking-wide">Catalogus verzonden <span className="text-gray-500 font-normal normal-case">{klantCatalogusLog.length} keer</span></div>
                  <a href="/artikels" className="text-xs text-[#002FA7] underline" data-testid="klant-catalogus-naar-artikels">Nieuwe catalogus maken →</a>
                </div>
                {klantCatalogusLog.length > 0 ? (
                  <table className="data-table text-xs" data-testid="klant-catalogus-log">
                    <thead><tr><th>Wanneer</th><th>Kanaal</th><th>Naar</th><th>Taal</th><th>Prijslijst</th><th>Bestand</th><th>Door</th></tr></thead>
                    <tbody>
                      {klantCatalogusLog.map(e => (
                        <tr key={e.id} data-testid={`klant-catalogus-log-${e.id}`}>
                          <td className="font-mono whitespace-nowrap">{e.verzonden_op ? new Date(e.verzonden_op).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                          <td>{e.kanaal === 'whatsapp' ? <span className="px-1.5 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-800">💬 WhatsApp</span> : e.kanaal === 'mail' ? <span className="px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-800">📧 Mail</span> : <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-700">{e.kanaal}</span>}</td>
                          <td className="font-mono">{e.to || '-'}</td>
                          <td className="uppercase">{e.taal}</td>
                          <td>{e.persoonlijk ? <span className="text-emerald-700 font-semibold">Persoonlijk</span> : <span className="text-gray-600">{e.prijsgroep || 'algemeen'}</span>}</td>
                          <td className="text-gray-500 truncate max-w-[200px]" title={e.fname}>{e.fname || '-'}</td>
                          <td className="text-gray-500">{e.door || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="text-center text-gray-400 py-8" data-testid="klant-catalogus-leeg">Nog geen catalogus naar deze klant gestuurd.<br /><span className="text-xs">Artikels → Catalogus PDF → kies deze klant → 📧 Mailen of 💬 WhatsApp.</span></div>}
              </div>
            )}

            {formTab === 'historiek' && (
              <div>
                {historiek.length > 0 ? (
                  <div className="space-y-2">{historiek.map((h, i) => (
                    <div key={i} className="p-3 bg-gray-50 border border-gray-100 text-xs">
                      <div className="flex items-center gap-2 mb-1"><ClockCounterClockwise size={12} className="text-gray-400" /><span className="font-mono text-gray-500">{h.tijdstip ? new Date(h.tijdstip).toLocaleString('nl-BE') : '-'}</span></div>
                      {Object.entries(h.wijzigingen || {}).map(([veld, val]) => (
                        <div key={veld} className="ml-5 text-gray-600"><strong>{veld}</strong>: <span className="text-red-400 line-through">{formatValue(val.oud)}</span> → <span className="text-[#059669]">{formatValue(val.nieuw)}</span></div>
                      ))}
                    </div>
                  ))}</div>
                ) : <div className="text-center text-gray-400 py-8">Geen historiek</div>}
              </div>
            )}

            {formTab === 'mails' && (
              <div className="mail-content" data-testid="klant-mails-tab">
                {klantMailsLoading && klantMails.length === 0 ? (
                  <div className="text-center text-gray-400 py-8 text-sm">⏳ Mails laden...</div>
                ) : klantMails.length === 0 ? (
                  <div className="text-center text-gray-400 py-12">
                    <div className="text-4xl mb-2">📭</div>
                    <div className="text-sm mb-1">Nog geen mailverkeer met deze klant.</div>
                    <div className="text-xs text-gray-300">
                      {form.email
                        ? `Eens je een mailbox koppelt in Mail Hub worden alle mails van/naar ${form.email} hier zichtbaar.`
                        : 'Voeg eerst een e-mailadres toe in het tabblad "Gegevens".'}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3" style={{ minHeight: 400 }}>
                    {/* Lijst */}
                    <div className="w-1/2 border border-gray-200 rounded overflow-y-auto" style={{ maxHeight: 600 }}>
                      <div className="px-3 py-2 bg-gray-50 border-b text-[10px] font-bold uppercase text-gray-500 flex items-center justify-between">
                        <span>{klantMails.length} bericht(en)</span>
                        <button onClick={loadKlantMails} className="text-[10px] text-[#002FA7] hover:underline" data-testid="klant-mails-refresh">🔄 Ververs</button>
                      </div>
                      {klantMails.map(m => {
                        const isActive = klantMailDetail?.id === m.id;
                        const isOutbound = m.direction === 'outbound';
                        return (
                          <button
                            key={m.id}
                            onClick={() => openKlantMail(m.id)}
                            className={`w-full text-left px-3 py-2 border-b text-xs transition-colors ${isActive ? 'bg-[#EEF3FF] border-l-2 border-l-[#002FA7]' : 'hover:bg-gray-50'} ${!m.is_read && !isOutbound ? 'font-semibold bg-blue-50/30' : ''}`}
                            data-testid={`klant-mail-${m.id}`}
                          >
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="truncate flex items-center gap-1">
                                <span className={`text-[10px] px-1 rounded ${isOutbound ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'}`}>
                                  {isOutbound ? '→' : '←'}
                                </span>
                                <span className="text-gray-700 truncate">{isOutbound ? (m.to_emails || []).join(', ') : (m.from_name || m.from_email)}</span>
                              </span>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">{(m.datum || '').slice(5, 16).replace('T', ' ')}</span>
                            </div>
                            <div className="text-gray-900 truncate mb-0.5">{m.subject || '(geen onderwerp)'}</div>
                            <div className="text-[10px] text-gray-500 truncate">{m.snippet}</div>
                            <div className="flex items-center gap-1 mt-1">
                              {m.ai_category && (
                                <span className="text-[9px] px-1 py-0.5 bg-purple-100 text-purple-700 rounded border border-purple-200">{m.ai_category}</span>
                              )}
                              {(m.attachments || []).length > 0 && <span className="text-[9px] text-gray-400">📎 {m.attachments.length}</span>}
                              {m.is_starred && <span className="text-amber-500 text-[10px]">⭐</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {/* Reader */}
                    <div className="flex-1 border border-gray-200 rounded overflow-y-auto p-4" style={{ maxHeight: 600 }}>
                      {klantMailDetailLoading && (
                        <div className="text-center text-gray-400 text-sm">⏳ Bericht laden...</div>
                      )}
                      {!klantMailDetail && !klantMailDetailLoading && (
                        <div className="text-center text-gray-400 text-sm pt-12">
                          <div className="text-3xl mb-2 opacity-40">📧</div>
                          Klik op een bericht om te lezen
                        </div>
                      )}
                      {klantMailDetail && !klantMailDetail.loading && (
                        <div>
                          <h3 className="text-base font-bold text-gray-900 mb-1">{klantMailDetail.subject || '(geen onderwerp)'}</h3>
                          <div className="text-xs text-gray-600 mb-2 flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{klantMailDetail.from_name || klantMailDetail.from_email}</span>
                            <span className="text-gray-400">&lt;{klantMailDetail.from_email}&gt;</span>
                            <span className="text-gray-400">•</span>
                            <span>{(klantMailDetail.datum || '').slice(0, 16).replace('T', ' ')}</span>
                          </div>
                          <div className="text-[11px] text-gray-500 mb-3 border-b pb-2">
                            Aan: {(klantMailDetail.to_emails || []).join(', ')}
                            {klantMailDetail.cc_emails?.length > 0 && <span> • Cc: {klantMailDetail.cc_emails.join(', ')}</span>}
                          </div>
                          {klantMailDetail.ai_summary && (
                            <div className="mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-800">
                              <Sparkle size={12} weight="fill" className="inline mr-1" /> AI: {klantMailDetail.ai_summary}
                            </div>
                          )}
                          {(klantMailDetail.attachments || []).length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-1.5">
                              {klantMailDetail.attachments.map(att => (
                                <a
                                  key={att.id}
                                  href={`#`}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                      const r = await API.get(`/mail/messages/${klantMailDetail.id}/attachment/${att.id}`, { responseType: 'blob' });
                                      const url = URL.createObjectURL(new Blob([r.data], { type: att.mime }));
                                      const a = document.createElement('a');
                                      a.href = url; a.download = att.filename;
                                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                      setTimeout(() => URL.revokeObjectURL(url), 1500);
                                    } catch (err) {
                                      alert('Download mislukt');
                                    }
                                  }}
                                  className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-[10px] hover:bg-gray-100 inline-flex items-center gap-1"
                                  title={att.filename}
                                >
                                  📎 {att.filename.length > 30 ? att.filename.slice(0, 28) + '…' : att.filename}
                                  {att.inkomende_document_id && <span className="text-green-600 ml-1">✓</span>}
                                </a>
                              ))}
                            </div>
                          )}
                          {klantMailDetail.html_body ? (
                            <iframe
                              title="klant-mail-body"
                              srcDoc={klantMailDetail.html_body}
                              sandbox=""
                              className="w-full border-0 bg-white"
                              style={{ minHeight: 280 }}
                            />
                          ) : (
                            <pre className="whitespace-pre-wrap text-xs text-gray-800 bg-gray-50 p-3 rounded">{klantMailDetail.text_body || klantMailDetail.snippet}</pre>
                          )}
                          {klantMailDetail.direction === 'inbound' && (
                            <div className="mt-3 pt-3 border-t text-right">
                              <a
                                href={`/mail`}
                                className="text-xs text-[#002FA7] hover:underline"
                                title="Open in Mail Hub om te antwoorden"
                              >→ Open in Mail Hub voor antwoord/AI-suggestie</a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Acties */}
            <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100">
              <button className="btn btn-primary" onClick={save} data-testid="save-klant-btn">Opslaan</button>
              <button className="btn btn-secondary" onClick={() => { save(); }} data-testid="save-klant-terug-btn">Opslaan en terug</button>
              <button className="btn btn-ghost ml-auto" onClick={() => { resetForm(); setShowForm(false); }}>Annuleren</button>
            </div>
          </div>
        </div>
      )}

      {/* ════ KLANTEN LIJST (alleen als formulier dicht is) ════ */}
      {!showForm && (
        <>
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1" style={{ maxWidth: 300 }}>
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input className="field-input" style={{ paddingLeft: 36 }} placeholder="Zoek op naam, bedrijf, BTW, verkoper..." value={q} onChange={e => setQ(e.target.value)} data-testid="klant-search" />
            </div>
            {verkopers.length > 0 && (
              <select
                className="field-input text-xs"
                value={verkoperFilter}
                onChange={(e) => setVerkoperFilter(e.target.value)}
                data-testid="verkoper-filter"
                title="Filter klanten op vaste verkoper (voor online bestellingen)"
                style={{ maxWidth: 240 }}
              >
                <option value="all">👤 Alle verkopers</option>
                <option value="geen">— Geen vaste verkoper —</option>
                {verkopers.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
              </select>
            )}
            <select
              className="field-input text-xs"
              value={shopFilter}
              onChange={(e) => setShopFilter(e.target.value)}
              data-testid="shop-filter"
              title="Filter klanten op shop-activiteit"
              style={{ maxWidth: 280 }}
            >
              <option value="all">🔍 Alle klanten</option>
              <option value="actief">🛒 Shop-account actief</option>
              <option value="recent">✓ Recent ingelogd (≤ 7 dagen)</option>
              <option value="inactief30">⚠️ Inactief &gt; 30 dagen</option>
              <option value="inactief90">⛔ Inactief &gt; 90 dagen</option>
              <option value="nooit">⊘ Nooit ingelogd</option>
            </select>
            {inst.iban_2 && (
              <select
                className="field-input text-xs"
                value={bankFilter}
                onChange={(e) => setBankFilter(e.target.value)}
                data-testid="bank-filter"
                title="Filter klanten op gekozen bankrekening"
                style={{ maxWidth: 200 }}
              >
                <option value="all">🏦 Alle bankrek.</option>
                <option value="standaard">{`Standaard (${(inst.iban || '').slice(0, 8)}...)`}</option>
                <option value="iban_2">{inst.factoring_actief ? `🏦 FACTOR (IBAN 2)` : `IBAN 2 (${(inst.iban_2 || '').slice(0, 8)}...)`}</option>
              </select>
            )}
            <span className="text-xs text-gray-500" data-testid="filter-count">{filtered.length} klant(en)</span>
          </div>

          {/* Bulk-selectie action bar — verschijnt wanneer ≥1 klant geselecteerd */}
          {selectedIds.size > 0 && (
            <div className="section-card mb-2" data-testid="bulk-action-bar">
              <div className="section-body flex items-center justify-between gap-3 py-2">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-semibold text-[#002FA7]">{selectedIds.size}</span>
                  <span className="text-gray-700">klant(en) geselecteerd</span>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-xs text-gray-500 underline hover:text-gray-700"
                    data-testid="bulk-clear-btn"
                  >Selectie wissen</button>
                </div>
                <button
                  type="button"
                  onClick={bulkDelete}
                  disabled={bulkDeleteBusy}
                  className="btn btn-danger btn-sm"
                  data-testid="bulk-delete-btn"
                >
                  {bulkDeleteBusy ? '⏳ Bezig…' : `🗑️ ${selectedIds.size} klant(en) verwijderen`}
                </button>
              </div>
            </div>
          )}

          <div className="section-card">
            <div className="section-body overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }} className="text-center">
                      <input
                        type="checkbox"
                        checked={paged.length > 0 && paged.every(k => selectedIds.has(k.id))}
                        onChange={() => toggleSelectAllVisible(paged)}
                        data-testid="bulk-select-all"
                        title="Selecteer alle klanten op deze pagina"
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th className="cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort('klant_nr')} data-testid="sort-nr">Nr<SortCaret col="klant_nr" /></th>
                    <th>Type</th>
                    <th className="cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort('naam')} data-testid="sort-naam">Naam / Bedrijf<SortCaret col="naam" /></th>
                    <th className="cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort('btwnr')} data-testid="sort-btw">BTW-nr<SortCaret col="btwnr" /></th>
                    <th className="cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort('stad')} data-testid="sort-stad">Adres<SortCaret col="stad" /></th>
                    <th>Contact</th>
                    <th>Korting</th>
                    <th>Termijn</th>
                    {inst.iban_2 && (
                      <th title="Welke bankrekening wordt op de factuur getoond?">Bank</th>
                    )}
                    <th className="cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort('shop_last_login')} data-testid="sort-shop-login" title="Sorteer op laatste shop-login">Shop-login<SortCaret col="shop_last_login" /></th>
                    <th className="cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort('updated_at')} data-testid="sort-updated">Laatst gewijzigd<SortCaret col="updated_at" /></th>
                    <th style={{ minWidth: 90, position: 'relative' }}>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span>Acties</span>
                        <div className="relative" ref={actiesHeaderTriggerRef}>
                          <div className="inline-flex items-center gap-1.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); openActiesHeaderMenu(); }} data-testid="toggle-acties-klanten">
                            <IOSToggle checked={anyActiesOn} onChange={() => toggleAllActies()} size="sm" />
                            <span className={`inline-flex items-center justify-center w-5 h-5 border border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7] text-gray-500 text-[10px] font-bold transition-colors ${actiesHeaderMenuOpen ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white'}`}>▼</span>
                          </div>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(k => {
                    const viesBlok = viesBlokken[k.id];
                    return (
                    <tr
                      key={k.id}
                      className={`cursor-pointer hover:bg-[#F0F4FF] transition-colors ${k.geblokkeerd ? 'klant-geblokkeerd-row' : viesBlok ? 'bg-red-50/60 hover:bg-red-100/50' : ''} ${selectedIds.has(k.id) ? 'bg-[#E8EEFF]' : ''}`}
                      onClick={() => startEdit(k)}
                      data-testid={`klant-row-${k.id}`}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(k.id)}
                          onChange={() => toggleSelected(k.id)}
                          data-testid={`bulk-select-${k.id}`}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td>
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#E8EEFF] text-[#002FA7] font-mono text-[10px] font-bold tracking-tight" data-testid={`klant-nr-pill-${k.id}`}>
                          {k.klant_nr || '-'}
                        </span>
                      </td>
                      <td><span className={`text-[10px] font-bold px-1.5 py-0.5 ${k.type_klant === 'particulier' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>{k.type_klant === 'particulier' ? 'PART' : 'BEDR'}</span>
                        {k.geblokkeerd && <Lock size={11} className="inline ml-1 text-red-500" />}
                        {viesBlok && (
                          <span
                            className="inline-flex items-center ml-1 text-red-600 cursor-help"
                            title={`VIES-blokkering: ${viesBlok.oud} → ${viesBlok.nieuw}. Bevestig via Notificaties om te deblokkeren.`}
                            data-testid={`vies-warn-${k.id}`}
                          >
                            <Warning size={13} weight="fill" />
                          </span>
                        )}
                      </td>
                      <td className="font-medium">
                        <span className={k.geblokkeerd ? 'klant-geblokkeerd-naam' : ''} data-testid={`klant-naam-${k.id}`}>{k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || '-'}</span>
                        {k.verkoper_id && verkoperNaam(k.verkoper_id) && (
                          <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 align-middle inline-flex items-center gap-0.5" title="Vaste verkoper (voor online bestellingen)" data-testid={`verkoper-badge-${k.id}`}>
                            👤 {verkoperNaam(k.verkoper_id)}
                          </span>
                        )}
                        {inst.factoring_actief && k.bank_keuze === 'iban_2' && (
                          <span
                            className="ml-2 text-[9px] font-bold px-1.5 py-0.5 bg-orange-500 text-white align-middle inline-flex items-center gap-0.5"
                            title="IBAN Factoring actief — facturen worden via factor verwerkt"
                            data-testid={`factor-badge-${k.id}`}
                          >
                            <Bank size={10} weight="bold" /> FACTOR
                          </span>
                        )}
                        {viesBlok && (
                          <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 bg-red-600 text-white align-middle" data-testid={`vies-badge-${k.id}`}>
                            GEBLOKKEERD VIA VIES
                          </span>
                        )}
                      </td>
                      <td className="font-mono text-xs">{k.btwnr || '-'}</td>
                      <td className="text-xs">{adresStr(k)}</td>
                      <td className="text-xs text-gray-500" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const mail = (k.email || '').split('\n')[0].trim();
                          const tel = (k.tel || '').trim();
                          const web = (k.website || '').trim();
                          return (
                            <span className="inline-flex flex-wrap items-center gap-1">
                              {mail && (
                                <a
                                  href={`mailto:${mail}`}
                                  className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                                  data-testid={`klant-email-${k.id}`}
                                  title={mail}
                                >
                                  <EnvelopeSimple size={11} /> {mail}
                                </a>
                              )}
                              {mail && tel && <span className="text-gray-300">|</span>}
                              {tel && (
                                <a
                                  href={`tel:${tel.replace(/\s+/g, '')}`}
                                  className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                                  data-testid={`klant-tel-${k.id}`}
                                  title={tel}
                                >
                                  <Phone size={11} /> {tel}
                                </a>
                              )}
                              {web && (
                                <>
                                  {(mail || tel) && <span className="text-gray-300">|</span>}
                                  <a
                                    href={web.startsWith('http') ? web : `https://${web}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                                    data-testid={`klant-web-${k.id}`}
                                    title={web}
                                  >
                                    <Globe size={11} /> {web.replace(/^https?:\/\//, '').split('/')[0]}
                                  </a>
                                </>
                              )}
                              {!mail && !tel && !web && <span className="text-gray-300">—</span>}
                            </span>
                          );
                        })()}
                        {(() => {
                          const kan = k.contact_kanalen || (k.contact_voorkeur === 'geen' ? [] : k.contact_voorkeur && k.contact_voorkeur !== 'auto' ? [k.contact_voorkeur] : ['email','sms','whatsapp']);
                          if (kan.length === 0) return <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 bg-gray-200 text-gray-700" title="Deze klant ontvangt geen automatische herinneringen">🚫 geen</span>;
                          if (kan.length < 3) return (
                            <span className="ml-1.5 inline-flex items-center gap-0.5" title={`Enkel: ${kan.join(', ')}`}>
                              {kan.includes('email') && <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-800 font-bold">✉️</span>}
                              {kan.includes('sms') && <span className="text-[9px] px-1 py-0.5 bg-purple-100 text-purple-800 font-bold">📱</span>}
                              {kan.includes('whatsapp') && <span className="text-[9px] px-1 py-0.5 bg-green-100 text-green-800 font-bold">💬</span>}
                            </span>
                          );
                          return null;
                        })()}
                      </td>
                      <td onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                        <select className="text-xs border border-gray-200 bg-white px-1.5 py-0.5 font-medium" style={{ width: 100 }}
                          value={k.kortinggroep || ''}
                          onChange={async (e) => { await API.put(`/klanten/${k.id}`, { kortinggroep: e.target.value }); load(); }}
                          data-testid={`kortinggroep-${k.id}`}>
                          <option value="">-</option>
                          {(prijsgroepen.length > 0 ? prijsgroepen : [
                            { key: 'basic', label: 'Basic' },
                            { key: 'standard', label: 'Standard' },
                            { key: 'premium', label: 'Premium' },
                            { key: 'bulk', label: 'Bulk' },
                          ]).map(g => {
                            // Capitaliseer label om consistent te blijven met bestaande data (backwards-compat: 'Basic', niet 'basic')
                            const labelCap = (g.label || g.key || '').charAt(0).toUpperCase() + (g.label || g.key || '').slice(1);
                            return <option key={g.key} value={labelCap}>{labelCap}</option>;
                          })}
                        </select>
                      </td>
                      <td className="text-xs text-center">{k.betalingstermijn || 30}d</td>
                      {inst.iban_2 && (
                        <td className="text-center" onClick={e => e.stopPropagation()}>
                          <select
                            className="text-[10px] font-bold border border-gray-300 px-1 py-0.5 bg-white hover:border-[#002FA7] cursor-pointer"
                            value={k.bank_keuze || ''}
                            onChange={async (e) => { await API.put(`/klanten/${k.id}`, { bank_keuze: e.target.value }); load(); }}
                            title={k.bank_keuze === 'iban_2' ? `IBAN 2: ${inst.iban_2}` : `Standaard: ${inst.iban}`}
                            data-testid={`bank-keuze-${k.id}`}
                          >
                            <option value="">Std</option>
                            <option value="iban_2">IBAN 2</option>
                          </select>
                        </td>
                      )}
                      <td className="text-[10px] whitespace-nowrap" data-testid={`shop-login-cell-${k.id}`}>
                        {(() => {
                          if (!k.shop_actief || !k.shop_password_hash) {
                            return <span className="text-gray-300">⊘ geen account</span>;
                          }
                          if (!k.shop_last_login) {
                            return <span className="text-amber-600 font-semibold" title="Wachtwoord aangemaakt maar nog niet ingelogd">⏳ nooit ingelogd</span>;
                          }
                          const d = daysSince(k.shop_last_login);
                          const dateStr = new Date(k.shop_last_login).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                          const color = d <= 7 ? 'text-green-600' : d <= 30 ? 'text-blue-600' : d <= 90 ? 'text-amber-600' : 'text-red-600';
                          const dot = d <= 7 ? '●' : d <= 30 ? '●' : d <= 90 ? '●' : '●';
                          const label = d === 0 ? 'vandaag' : d === 1 ? 'gisteren' : `${d}d geleden`;
                          return (
                            <span className={`${color} font-semibold inline-flex items-center gap-1`} title={`Laatst ingelogd: ${dateStr}${k.shop_login_count ? ` (${k.shop_login_count}× totaal)` : ''}`}>
                              <span>{dot}</span> {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="text-[10px] text-gray-400 font-mono whitespace-nowrap">
                        {(k.updated_at || k.created_at) ? new Date(k.updated_at || k.created_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          {!anyActiesOn && <span className="text-[10px] text-gray-300">—</span>}
                          {actiesVisible.portal && (
                          <button className="btn btn-ghost btn-sm text-[#002FA7]" title="Portal-link voor deze klant" onClick={async () => {
                            const r = await API.post(`/klanten/${k.id}/portal-link`).catch(() => null);
                            if (!r?.data) { alert('Fout bij genereren portal-link'); return; }
                            const url = `${window.location.origin}${r.data.portal_url}`;
                            const msg = r.data.email_sent
                              ? `Portal-link verzonden naar ${k.email}\n\n${url}`
                              : `Portal-link aangemaakt (klant heeft geen e-mailadres of SendGrid niet geconfigureerd):\n\n${url}\n\nLink is gekopieerd naar klembord.`;
                            try { await navigator.clipboard.writeText(url); } catch {}
                            alert(msg);
                          }} data-testid={`portal-klant-${k.id}`}>🔗</button>
                          )}
                          {actiesVisible.shop_invite && (
                          <button
                            className="btn btn-ghost btn-sm text-green-600"
                            title={k.shop_actief ? `Shop-toegang actief (${k.shop_login_email || k.email})` : 'Verstuur shop-uitnodiging voor online bestellen'}
                            onClick={async () => {
                              if (!k.email) { alert('Klant heeft geen e-mailadres in zijn fiche'); return; }
                              if (!window.confirm(`Stuur shop-uitnodiging naar ${k.email}?\n\nKlant ontvangt een e-mail met setup-link om een wachtwoord aan te maken.`)) return;
                              try {
                                const r = await API.post(`/klanten/${k.id}/shop-uitnodiging`);
                                alert(`Uitnodiging verstuurd naar ${r.data.email}!\n\nSetup-link (48u geldig):\n${r.data.setup_url}`);
                                try { await navigator.clipboard.writeText(r.data.setup_url); } catch {}
                                load();
                              } catch (e) {
                                alert('Fout: ' + (e.response?.data?.detail || e.message));
                              }
                            }}
                            data-testid={`shop-invite-${k.id}`}
                          >🛒{k.shop_actief && '✓'}</button>
                          )}
                          {k.shop_actief && actiesVisible.shop_impersonate && (
                              <button
                                className="btn btn-ghost btn-sm text-blue-600"
                                title={`Open shop als ${k.bedrijf || k.voorn} (zonder wachtwoord)`}
                                onClick={async () => {
                                  try {
                                    const r = await API.post(`/klanten/${k.id}/shop-impersonate`);
                                    localStorage.setItem('shop_token', r.data.token);
                                    localStorage.removeItem('shop_cart');
                                    window.open('/shop/bestellingen', '_blank');
                                  } catch (e) {
                                    alert('Impersoneren mislukt: ' + (e.response?.data?.detail || e.message));
                                  }
                                }}
                                data-testid={`shop-impersonate-${k.id}`}
                              >👁️</button>
                          )}
                          {k.shop_actief && actiesVisible.shop_resetpw && (
                              <button
                                className="btn btn-ghost btn-sm text-amber-600"
                                title="Reset wachtwoord (krijg nieuw tijdelijk wachtwoord)"
                                onClick={async () => {
                                  if (!window.confirm(`Wachtwoord resetten voor ${k.bedrijf || k.voorn}?\n\nEr wordt een nieuw tijdelijk wachtwoord aangemaakt dat u zelf moet doorgeven.`)) return;
                                  try {
                                    const r = await API.post(`/klanten/${k.id}/shop-reset-pw`);
                                    try { await navigator.clipboard.writeText(`Email: ${r.data.email}\nWachtwoord: ${r.data.password}`); } catch {}
                                    alert(`✓ Nieuw wachtwoord gegenereerd (en gekopieerd naar klembord):\n\nE-mail: ${r.data.email}\nWachtwoord: ${r.data.password}\n\n⚠ Bewaar dit zorgvuldig — het wordt niet opnieuw getoond.`);
                                  } catch (e) {
                                    alert('Reset mislukt: ' + (e.response?.data?.detail || e.message));
                                  }
                                }}
                                data-testid={`shop-resetpw-${k.id}`}
                              >🔑</button>
                          )}
                          {actiesVisible.excel && (
                              <button
                                className="btn btn-ghost btn-sm text-emerald-600"
                                title="Excel-export aankoophistoriek (per artikel + per factuurregel)"
                                onClick={() => {
                                  setExcelKlant(k);
                                  setExcelVan('');
                                  setExcelTot('');
                                }}
                                data-testid={`excel-klant-${k.id}`}
                              >📊</button>
                          )}
                          {actiesVisible.edit && (
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(k)} data-testid={`edit-klant-${k.id}`}><PencilSimple size={14} /></button>
                          )}
                          {actiesVisible.duplicate && (
                          <button className="btn btn-ghost btn-sm text-amber-600" title="Verplaats naar leveranciers" onClick={() => moveToLeverancier(k)} data-testid={`move-klant-${k.id}`}><ArrowBendUpRight size={14} /></button>
                          )}
                          {actiesVisible.delete && (
                          <button className="btn btn-ghost btn-sm text-red-400" onClick={() => del(k.id)} data-testid={`del-klant-${k.id}`}><Trash size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan="10" className="text-center text-gray-400 py-8">Geen klanten</td></tr>}
                </tbody>
              </table>
            </div>
            <Pagination total={filtered.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} />
          </div>
        </>
      )}

      {/* Notitie / Contact-log modal */}
      {notitieModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setNotitieModal(null)}>
          <div className="w-full max-w-lg bg-white shadow-2xl" onClick={e => e.stopPropagation()} data-testid="klant-notitie-modal">
            <div className="px-5 py-3 border-b border-gray-200 bg-[#002FA7] text-white flex items-center justify-between">
              <h3 className="text-sm font-bold">✎ {notitieModal.id ? 'Contact bewerken' : 'Contact registreren'} — {form.bedrijf || `${form.voorn} ${form.achtern}`.trim()}</h3>
              <button onClick={() => setNotitieModal(null)} className="text-white hover:opacity-70">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">Type</label>
                  <select
                    className="field-input text-sm w-full"
                    value={notitieModal.type}
                    onChange={e => setNotitieModal(p => ({ ...p, type: e.target.value }))}
                    data-testid="klant-notitie-type"
                  >
                    <option value="telefoon">📞 Telefoon</option>
                    <option value="email">✉️ E-mail</option>
                    <option value="afspraak">📅 Afspraak</option>
                    <option value="notitie">📝 Notitie</option>
                    <option value="overig">• Overig</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">Datum/uur</label>
                  <input
                    type="datetime-local"
                    className="field-input text-sm w-full"
                    value={notitieModal.datum}
                    onChange={e => setNotitieModal(p => ({ ...p, datum: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">
                  Wat werd besproken?
                </label>
                <textarea
                  className="field-input text-sm w-full"
                  rows={5}
                  placeholder="Bv. Klant belooft factuur te betalen voor eind week. Graag herinneren op vrijdag."
                  value={notitieModal.omschr}
                  onChange={e => setNotitieModal(p => ({ ...p, omschr: e.target.value }))}
                  data-testid="klant-notitie-omschr"
                />
              </div>
              {notitieModal.factuur_refs?.length > 0 && (
                <div className="text-[11px] text-gray-600 flex items-center gap-2 flex-wrap">
                  <span className="font-bold">Gekoppelde facturen:</span>
                  {notitieModal.factuur_refs.map(r => (
                    <span key={r} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 font-mono text-[10px]">{r}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => setNotitieModal(null)}>Annuleren</button>
              <button className="btn btn-primary btn-sm" onClick={saveNotitie} data-testid="klant-notitie-save">
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}
      <KlantImportModal
        open={showAiImport}
        onClose={() => setShowAiImport(false)}
        onDone={() => { load(); setImportMsg('Import uitgevoerd'); setTimeout(() => setImportMsg(''), 3000); }}
      />

      <BetalingsherinneringModal
        open={bhModalOpen}
        onClose={() => setBhModalOpen(false)}
        klantId={editId}
        klantNaam={form.bedrijf || form.naam || ''}
        klantEmail={form.email || ''}
        klantGsm={form.gsm || form.mobiel || form.tel || ''}
      />

      {/* Acties dropdown - fixed position overlay (renders outside overflow container) */}
      {actiesHeaderMenuOpen && (
        <div
          ref={actiesHeaderMenuRef}
          className="fixed bg-white border border-gray-200 shadow-2xl z-50 w-[320px]"
          style={{ top: actiesHeaderMenuPos.top, right: actiesHeaderMenuPos.right }}
          onClick={e => e.stopPropagation()}
          data-testid="klanten-acties-menu"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Zichtbare acties</span>
            <button type="button" className="text-[11px] text-[#002FA7] hover:underline font-bold" onClick={toggleAllActies}>
              {anyActiesOn ? 'Alles uit' : 'Alles aan'}
            </button>
          </div>
          {[
            { k: 'portal', label: '🔗 Portal-link (klant-portaal)' },
            { k: 'shop_invite', label: '🛒 Shop-uitnodiging' },
            { k: 'shop_impersonate', label: '👁️ Open shop als klant' },
            { k: 'shop_resetpw', label: '🔑 Reset shop-wachtwoord' },
            { k: 'excel', label: '📊 Excel-export aankopen' },
            { k: 'edit', label: '✏️ Bewerken' },
            { k: 'duplicate', label: '↪️ Verplaats naar leveranciers' },
            { k: 'delete', label: '🗑️ Verwijderen' },
          ].map(a => (
            <div key={a.k} className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-gray-50 text-[13px] text-gray-700" data-testid={`klanten-acties-opt-${a.k}`}>
              <span className="flex-1 font-medium">{a.label}</span>
              <IOSToggle checked={!!actiesVisible[a.k]} onChange={() => toggleActie(a.k)} size="sm" showLabel={false} />
            </div>
          ))}
        </div>
      )}

      {excelKlant && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !excelLoading && setExcelKlant(null)}
          data-testid="excel-export-modal"
        >
          <div
            className="bg-white shadow-2xl border border-gray-200 w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">📊 Excel — Aankoophistoriek</h3>
              <button
                className="text-gray-400 hover:text-gray-700"
                onClick={() => !excelLoading && setExcelKlant(null)}
                disabled={excelLoading}
                data-testid="excel-export-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mb-4 text-sm text-gray-600">
              <span className="font-bold text-gray-900">{excelKlant.bedrijf || `${excelKlant.voorn || ''} ${excelKlant.achtern || ''}`.trim()}</span>
            </div>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Van datum (optioneel)</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={excelVan}
                  onChange={(e) => setExcelVan(e.target.value)}
                  disabled={excelLoading}
                  data-testid="excel-export-van"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Tot datum (optioneel)</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={excelTot}
                  onChange={(e) => setExcelTot(e.target.value)}
                  disabled={excelLoading}
                  data-testid="excel-export-tot"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50"
                  onClick={() => { setExcelVan(''); setExcelTot(''); }}
                  disabled={excelLoading}
                  data-testid="excel-quick-alles"
                >Alles</button>
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50"
                  onClick={() => {
                    const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth(), 1);
                    setExcelVan(start.toISOString().slice(0,10)); setExcelTot(d.toISOString().slice(0,10));
                  }}
                  disabled={excelLoading}
                  data-testid="excel-quick-mtd"
                >Deze maand</button>
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50"
                  onClick={() => {
                    const d = new Date(); const start = new Date(d.getFullYear(), 0, 1);
                    setExcelVan(start.toISOString().slice(0,10)); setExcelTot(d.toISOString().slice(0,10));
                  }}
                  disabled={excelLoading}
                  data-testid="excel-quick-ytd"
                >Dit jaar</button>
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50"
                  onClick={() => {
                    const d = new Date(); const start = new Date(d); start.setMonth(d.getMonth()-12);
                    setExcelVan(start.toISOString().slice(0,10)); setExcelTot(d.toISOString().slice(0,10));
                  }}
                  disabled={excelLoading}
                  data-testid="excel-quick-12m"
                >Laatste 12m</button>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mb-4">
              Excel bevat 2 tabs: <strong>Overzicht</strong> (geaggregeerd per artikel) + <strong>Detail</strong> (één rij per factuurregel).
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setExcelKlant(null)}
                disabled={excelLoading}
                data-testid="excel-export-cancel"
              >Annuleer</button>
              <button
                className="btn btn-primary btn-sm"
                disabled={excelLoading}
                onClick={downloadAankoopExcel}
                data-testid="excel-export-download"
              >
                {excelLoading ? 'Bezig…' : 'Download Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
